import { onBrandNav, takePendingBrandNav } from "../utils/brandNavSignal.js";
import { getSearchModalShown, subscribeSearchModalShown } from "../utils/searchModalSignal.js";
import { forwardRef, memo, useCallback, useMemo, useRef, useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { formatDisplayPrice } from "../utils/currency.js";
import { useCart } from "../context/CartContext.jsx";
import { useAudio } from "../context/AudioContext.jsx";
import {
  fetchItems,
  fetchSoldItems,
  fetchBrandBackgrounds,
  getCachedItems,
  getShowcaseHiddenBrands,
  invalidateInventoryCache,
} from "../utils/inventoryWarmup.js";
import Footer from "./Footer.jsx";
import { isMobileViewport, isTouchDevice } from "../utils/viewport.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { MUSIC_ENABLED } from "../config.js";

// Re-exported for any external caller still using the old name.
export const invalidateCarouselCache = invalidateInventoryCache;

function extractSizeLabel(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  return s.includes(",") ? s.split(",")[0].trim() : s;
}

function getImageUrl(item) {
  const photos = Array.isArray(item.photos_json) ? item.photos_json : [];
  const ci = typeof item.cover_index === "number" ? item.cover_index : 0;
  return photos[ci]?.url || photos[0]?.url || null;
}

// ── Showcase image loading ──
// Every backdrop and product photo the showcase needs goes through one queue
// (preloadImages): fetched and decoded ahead of time, a few at a time, in the
// order they are likely to be wanted, so previewing or moving to a brand does
// not wait on the network or the decoder. The images are large (about 1-2 MB
// each), so the order matters more than the count; BrandCarousel's preload
// effects set it.
//
// A brand's "stage" is its backdrop plus the photos of the cards on screen
// when its row comes up (stageUrls). useStageReady holds the backdrop and the
// cards until the whole stage is in, then both come up in the same frame:
// the backdrop never lands behind cards that are already showing, and cards
// never sit on black waiting for a backdrop. A stage still not in
// STAGE_WAIT_CAP_MS after its row is on screen shows what it has, so a slow
// or broken image cannot hold a brand back for long.
const PRELOAD_CONCURRENCY = 4;
const STAGE_WAIT_CAP_MS = 2500;
const imageEntries = new Map(); // url -> { img, settled, ok }
const imageListeners = new Set();
let preloadQueue = []; // [{ url, keep }], next first
let preloadInFlight = 0;

function subscribeImages(cb) {
  imageListeners.add(cb);
  return () => { imageListeners.delete(cb); };
}
// Settled: in (loaded and decoded) or failed. Decoded: in and ready to paint.
function imageSettled(url) {
  const e = imageEntries.get(url);
  return !!e && e.settled;
}
function imageDecoded(url) {
  const e = imageEntries.get(url);
  return !!e && e.settled && e.ok;
}

function startImage(url, keep) {
  const img = new Image();
  img.decoding = "async";
  img.fetchPriority = keep ? "high" : "low";
  const entry = { img, settled: false, ok: false };
  imageEntries.set(url, entry);
  preloadInFlight++;
  const finish = (ok) => {
    if (entry.settled) return;
    entry.settled = true;
    entry.ok = ok;
    // Backdrops and stage photos stay held, which keeps the browser from
    // dropping them before they are shown; the rest only had to reach the
    // HTTP cache.
    if (!keep) entry.img = null;
    preloadInFlight--;
    imageListeners.forEach((cb) => cb());
    pumpPreload();
  };
  img.src = url;
  if (img.decode) {
    // decode() also rejects for an image that loaded but would not decode
    // off-screen. That one still paints, so it counts as in.
    img.decode().then(() => finish(true), () => finish(img.complete && img.naturalWidth > 0));
  } else {
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
  }
}

function pumpPreload() {
  while (preloadInFlight < PRELOAD_CONCURRENCY && preloadQueue.length) {
    const { url, keep } = preloadQueue.shift();
    if (!imageEntries.has(url)) startImage(url, keep);
  }
}

// Queue urls in the order given, skipping any already asked for. first:
// ahead of everything waiting (a brand about to be shown), not behind it.
// keep: hold the image once it is in (see startImage).
function preloadImages(urls, { first = false, keep = false } = {}) {
  const fresh = [];
  const seen = new Set();
  for (const url of urls) {
    if (!url || seen.has(url) || imageEntries.has(url)) continue;
    seen.add(url);
    fresh.push({ url, keep });
  }
  if (!fresh.length) return;
  if (first) {
    preloadQueue = [...fresh, ...preloadQueue.filter((q) => !seen.has(q.url))];
  } else {
    for (const q of fresh) {
      const queued = preloadQueue.find((p) => p.url === q.url);
      if (!queued) preloadQueue.push(q);
      else if (keep) queued.keep = true;
    }
  }
  pumpPreload();
}

// Drops everything still waiting (images already on their way finish).
function clearPreloadQueue() {
  preloadQueue = [];
}

// The cards on screen when a brand's row comes up: its track opens on the
// middle item (rows are arranged centre-out) with up to three cards either
// side on a desktop-wide track, two on a tablet and one on a phone (the
// track's SIDE_COUNT). A short row shows every item.
function stagePhotoUrls(items) {
  const n = items.length;
  if (!n) return [];
  const w = typeof window !== "undefined" ? window.innerWidth : 1440;
  const side = w >= 1024 ? 3 : w >= 640 ? 2 : 1;
  const mid = Math.floor(n / 2);
  const out = [];
  for (let i = Math.max(0, mid - side); i <= Math.min(n - 1, mid + side); i++) {
    const url = getImageUrl(items[i]);
    if (url) out.push(url);
  }
  return out;
}

function backdropUrl(bg) {
  return bg && bg.type !== "video" ? bg.url : null;
}

function stageUrls(bg, items) {
  const b = backdropUrl(bg);
  return b ? [b, ...stagePhotoUrls(items)] : stagePhotoUrls(items);
}

// The stage gate. True once every url is in (or failed), or once the row has
// been on screen (active) for STAGE_WAIT_CAP_MS. A row on screen whose stage
// is not in moves it to the front of the queue. waiting: the stage is not
// known yet (the backdrops list has not arrived), so it cannot be in, though
// the cap still runs. Latches: once true it stays true for the life of the
// component, so a later change (an inventory refresh) never hides what is
// already showing.
function useStageReady(urls, { active = true, waiting = false } = {}) {
  const key = urls.join("\n");
  const settled = useSyncExternalStore(subscribeImages, () => urls.every(imageSettled));
  const [capped, setCapped] = useState(false);
  const readyRef = useRef(false);
  useEffect(() => {
    if (!active || readyRef.current) return undefined;
    if (!waiting) preloadImages(urls, { first: true, keep: true });
    const timer = setTimeout(() => setCapped(true), STAGE_WAIT_CAP_MS);
    return () => clearTimeout(timer);
    // urls is read through key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, active, waiting]);
  if ((settled && !waiting) || capped) readyRef.current = true;
  return readyRef.current;
}

function getHoverUrl(item) {
  const photos = Array.isArray(item.photos_json) ? item.photos_json : [];
  if (photos.length <= 1) return null;
  const ci = typeof item.cover_index === "number" ? item.cover_index : 0;
  return photos[(ci + 1) % photos.length]?.url || null;
}

// Desktop /brand idle window: the header stays up while the user is active on
// any brand panel and only tucks away after this much silence.
const HEADER_IDLE_MS = 8000;
// First-visit peek on the brand title (see the peek effect in BrandCarousel).
// The schedule is the wait BEFORE each step, measured from the previous one,
// so [3000, 6000] peeks at 3s and again at 9s. TITLE_PEEK_MS is how long one
// peek takes. Latched per page load, module-level so a remount cannot replay
// it. Matches the catalog's FEATURED -> NEW ARRIVALS peek (FeaturedPage). It
// names what is NEXT: the brand the arrows, a swipe or the wheel go to.
// The strip breathes for 1.4s; the class is held a second longer so the
// up-next brand can light after it (app.css, .brand-row--title-peek).
// The side arrows' clearance from the centre card's edges (placeSideArrows).
const SIDE_ARROW_GAP_PX = 8;
const TITLE_PEEK_SCHEDULE_MS = [3000, 6000];
const TITLE_PEEK_MS = 2400;
let titlePeekShownThisLoad = false;
// Off (the owner, 2026-10-17: "get rid of this on showcase", the next
// brand's name fading in beside the title): no peek at all, so neither the
// name nor the arrows' and UP NEXT's lighting runs. true brings it back.
const TITLE_PEEK_ENABLED = false;
// Distance between two panel indices around the ring of n panels: the first
// and last brands are neighbours, so the seamless wrap (wrapToBrand) always
// finds the incoming panel's background mounted.
const ringDistance = (a, b, n) => {
  const d = Math.abs(a - b);
  return n > 0 ? Math.min(d, n - d) : d;
};
// A brand title's first letter starts its own side bearing in from the
// title's box (TeX Gyre Heros at 48px: 3px for B, D, E, N, R; 1px for C, G),
// so the name sat right of the rule and the count under it. This is that gap
// as a fraction of the font size, for the face the titles are drawn in
// (`font`, a canvas font at 200px); app.css pulls the title, and the strip
// laid over it, left by it (--title-ink). A letter that already overhangs
// (S) stays put. Cached per face and letter; cleared when a font loads, since
// a face still loading measures as its fallback.
const titleInkCache = new Map();
let titleInkCtx = null;
function titleInkEm(font, name) {
  const ch = (name || "").trim().charAt(0).toUpperCase();
  if (!font || !ch) return 0;
  const key = `${font}|${ch}`;
  let em = titleInkCache.get(key);
  if (em === undefined) {
    titleInkCtx ||= document.createElement("canvas").getContext("2d");
    titleInkCtx.font = font;
    em = Math.max(0, -titleInkCtx.measureText(ch).actualBoundingBoxLeft) / 200;
    titleInkCache.set(key, em);
  }
  return em;
}

// The showcase list's previews crossfade the page behind its glass instead of
// cutting to the new brand (the owner, 2026-09-30: "when switching between
// brands transition quickly but smoothly between backgrounds we can see"). A
// view transition: the browser holds a picture of the page as it was, the
// update lands the jump, and the two fade across (app.css, the
// :root.brand-crossfade rules). The class stays on while any is running, so
// one overtaken by the next keeps its timing. Without the View Transitions
// API, or with motion reduced, the update simply runs: a cut, as before.
let crossfadesRunning = 0;
function crossfadeTo(update) {
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (typeof document === "undefined" || typeof document.startViewTransition !== "function" || reduced) {
    update();
    return;
  }
  const root = document.documentElement;
  crossfadesRunning += 1;
  root.classList.add("brand-crossfade");
  const transition = document.startViewTransition(update);
  // Overtaken by the next one, a transition's ready rejects; that is expected.
  transition.ready.catch(() => {});
  transition.finished.finally(() => {
    crossfadesRunning -= 1;
    if (!crossfadesRunning) root.classList.remove("brand-crossfade");
  });
}

// Whether the pointer can hover. Where it can, the title strip opens on
// hover and the title's press does nothing (hoverOpenStrip, in the carousel).
const hasHoverPointer = () =>
  typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
// How long the strip stays out once the pointer has left the title and names.
const STRIP_HOVER_GRACE_MS = 250;
// Whether hovering the title opens the brand strip. Off: the owner removed it
// (2026-10-02). With it off and no press on a pointer device, the strip does
// not open on desktop at all.
const STRIP_ON_HOVER = false;

const ProductCard = memo(function ProductCard({ item, inCart, onToggleCart, style }) {
  const src = getImageUrl(item);
  const hoverSrc = getHoverUrl(item);
  const priceUsd = item.price_usd;
  const priceFormatted = priceUsd != null ? formatDisplayPrice(priceUsd, "USD") : null;
  const originalFormatted = item.price_adjusted && item.original_price_usd
    ? formatDisplayPrice(item.original_price_usd, "USD")
    : null;
  const isSold = item.sold || item.state === "SOLD";
  // Hover image stays unmounted until the pointer actually enters the card,
  // so off-screen duplicates in the LOOP_COPIES=5 carousel don't decode a
  // second image into memory.
  const [hoverReady, setHoverReady] = useState(false);
  // Mercari-style: each card image starts invisible and fades in on its own
  // onLoad. Cached images fire onLoad on the first paint so they appear
  // instantly; cold loads fade in cleanly as bytes arrive — same UX whether
  // the network is fast or slow.
  const [mainLoaded, setMainLoaded] = useState(() => !!src && imageDecoded(src));

  return (
    // `state.from` lets the product page's back arrow return here. The
    // carousel only ever renders on /showcase (FeaturedGatewayPage).
    <Link
      to={`/featured/item/${item.id}`}
      state={{ from: "/showcase" }}
      className={`brand-row__card${isSold ? " brand-row__card--sold" : ""}`}
      draggable={false}
      style={style}
      onPointerEnter={hoverSrc && !hoverReady ? () => setHoverReady(true) : undefined}
    >
      <div className="brand-row__img">
        {src ? (
          <>
            <img
              src={src}
              alt={item.title || ""}
              draggable={false}
              className="brand-row__img-main"
              loading="lazy"
              decoding="async"
              // Only set inline opacity while loading. Clearing it once
              // loaded lets the CSS hover-swap rule win — otherwise
              // inline `opacity: 1` would beat the hover selector and
              // the swap to the hover image would never trigger.
              style={mainLoaded ? undefined : { opacity: 0 }}
              onLoad={() => setMainLoaded(true)}
              onError={() => setMainLoaded(true)}
            />
            {hoverSrc && hoverReady && (
              <img
                src={hoverSrc}
                alt=""
                draggable={false}
                className="brand-row__img-hover"
                loading="lazy"
                decoding="async"
              />
            )}
          </>
        ) : (
          <div className="brand-row__placeholder" />
        )}
        {/* No SOLD badge over the photo: the SOLD OUT box under the price
            (below) is the one sold mark, site-wide (the owner, 2026-09-30). */}
      </div>
      <div className="brand-row__card-info">
        <div className="brand-row__card-text">
          <div className="brand-row__card-title">{item.title || "Untitled"}</div>
          {extractSizeLabel(item.size) && (
            <div className="brand-row__card-size">{extractSizeLabel(item.size)}</div>
          )}
          {originalFormatted ? (
            <div className="brand-row__card-price-container">
              <span className="brand-row__card-price brand-row__card-price--original">{originalFormatted}</span>
              <span className="brand-row__card-price">{priceFormatted}</span>
            </div>
          ) : (
            priceFormatted && <div className="brand-row__card-price">{priceFormatted}</div>
          )}
          {/* The catalog card's SOLD OUT box (ProductCard, .sold-out), in the
              place the cart button takes on a piece still for sale. */}
          {isSold && <div className="sold-out">Sold out</div>}
        </div>
        {!isSold && (
          <button
            type="button"
            className={`brand-row__cart-toggle${inCart ? " brand-row__cart-toggle--active" : ""}`}
            aria-label={inCart ? "Remove from cart" : "Add to cart"}
            style={{ touchAction: "manipulation" }}
            onPointerDown={(e) => {
              // Intercept before the Link's click handler runs. Using pointerDown
              // (not click) so a quick mobile tap on the X always fires even if
              // the carousel track's touch-action pan-x eats the synthesized click.
              e.preventDefault();
              e.stopPropagation();
              onToggleCart(item);
            }}
            onClick={(e) => {
              // Still stop click bubbling so the Link doesn't navigate on devices
              // where pointerDown didn't fire first.
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerEnter={(e) => e.currentTarget.closest(".brand-row__card")?.classList.add("brand-row__card--no-hover")}
            onPointerLeave={(e) => e.currentTarget.closest(".brand-row__card")?.classList.remove("brand-row__card--no-hover")}
          ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg></button>
        )}
      </div>
    </Link>
  );
});

// Per-row audio toggle with the same visualizer as the global TrapVinyl.
// Renders one button per brand-row but only runs the analyser tick when
// `active` is true, so out-of-view rows don't burn frames. Mirrors TrapVinyl's
// audio-toggle classes so the existing icon + bars CSS applies; an extra
// --brand-row modifier flips the position from fixed to absolute (within
// the parent brand-row section) and bumps z-index above the nav-zone bar.
function BrandRowAudio({ active }) {
  const { playing, toggle, analyserRef } = useAudio();
  const barRefs = useRef([]);
  const rafRef = useRef(null);
  const dataRef = useRef(null);
  const BAR_COUNT = 7;

  useEffect(() => {
    if (!active || !playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      for (const bar of barRefs.current) {
        if (bar) bar.style.height = "";
      }
      return;
    }
    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    if (!dataRef.current || dataRef.current.length !== bufferLength) {
      dataRef.current = new Uint8Array(bufferLength);
    }

    const BASS_START = 1;
    const BASS_END = Math.min(24, bufferLength);
    const BASS_BOOST = 1.6;
    const MAX_H = 45;
    const FALLOFF = [1.0, 0.92, 0.84, 0.75, 0.66, 0.58, 0.5, 0.43, 0.37, 0.31, 0.26, 0.22, 0.19, 0.17, 0.15, 0.14, 0.13];
    const ownBands = Array.from({ length: BAR_COUNT }, (_, i) => {
      const t = i / BAR_COUNT;
      const start = Math.max(1, Math.round(4 + t * 80));
      const end = Math.max(start + 2, Math.round(4 + ((i + 1) / BAR_COUNT) * 80));
      return { start: Math.min(start, bufferLength - 1), end: Math.min(end, bufferLength), boost: 1.4 + t * 2.2 };
    });
    const ownWeightByDist = [0.15, 0.20, 0.25, 0.32, 0.38, 0.44, 0.49, 0.53, 0.56, 0.59, 0.61, 0.62, 0.63, 0.64, 0.65, 0.66, 0.67];

    const tick = () => {
      analyser.getByteFrequencyData(dataRef.current);
      let bassSum = 0;
      for (let j = BASS_START; j < BASS_END; j++) bassSum += dataRef.current[j];
      const bassAvg = bassSum / (BASS_END - BASS_START);
      const bassN = Math.min(1, (bassAvg / 255) * BASS_BOOST);

      for (let i = 0; i < BAR_COUNT; i++) {
        const distFromRight = BAR_COUNT - 1 - i;
        const falloff = FALLOFF[Math.min(distFromRight, FALLOFF.length - 1)];
        const { start, end, boost } = ownBands[i];
        let ownSum = 0;
        for (let j = start; j < end; j++) ownSum += dataRef.current[j];
        const ownAvg = ownSum / (end - start);
        const ownN = Math.min(1, (ownAvg / 255) * boost);
        const ownWeight = ownWeightByDist[Math.min(distFromRight, ownWeightByDist.length - 1)];
        const combined = Math.min(1, bassN * falloff * (1 - ownWeight) + ownN * ownWeight);
        const height = Math.max(2, Math.round(2 + combined * MAX_H));
        const bar = barRefs.current[i];
        if (bar) bar.style.height = `${height}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active, playing, analyserRef]);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={playing ? "Pause audio" : "Play audio"}
      className={`audio-toggle audio-toggle--brand-row${playing ? " audio-toggle--playing" : ""}`}
    >
      <span className="audio-toggle__icon audio-toggle__icon--music" aria-hidden="true">
        <svg width="23" height="23" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3z" />
        </svg>
      </span>
      <span className="audio-toggle__icon audio-toggle__icon--play" aria-hidden="true">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 5 L19 12 L7 19 Z" />
        </svg>
      </span>
      <span className="audio-toggle__bars" aria-hidden="true">
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <span
            key={i}
            ref={(el) => { barRefs.current[i] = el; }}
            className="audio-toggle__bar"
          />
        ))}
      </span>
    </button>
  );
}

// Native horizontal scroll + LOOP_COPIES copies of items in the DOM. We anchor
// scrollLeft into the middle copy and silently jump back/forward by one set
// width when scroll crosses a safety boundary — that's the "rightmost item
// refills on the left" behaviour. The arc visual is layered on top per frame.
const LOOP_COPIES = 5;
const LOOP_ANCHOR = 2;

// resetKey: bumped by the dice shuffle. Reordering the brand sections MOVES
// each track's DOM node, and a moved element's scrollLeft silently resets to
// 0 (no scroll event fires) — the cards keep their old arc transforms and
// end up thousands of px off-screen, reading as an empty row. Re-running the
// main effect re-centers and re-arcs the track (under the shuffle blackout).
// staggerMs: the gap between one card's reveal and the next (the strip
// preview uses 0, all at once with the backdrop). bg, active and waiting feed
// the stage gate (useStageReady): the cards stay hidden until the brand's
// backdrop and on-screen photos are in, and come up with the backdrop.
// active: the row has been on screen (the gate's cap counts from then).
// waiting: the backdrops list has not arrived, so whether this brand has a
// backdrop is not known yet.
// memo: the carousel re-renders on every change of the brand in view
// (nearestBrand, snappedBrand, the footer flag), and each render re-rendered
// every card of every brand, about 325 of them, each with a fresh style
// object, when a swipe landed. Its props (the row's items, its backdrop,
// the cart set and handler, the flags) do not change with the brand in view,
// so a track now renders only when something it shows does.
const BrandRowTrack = memo(function BrandRowTrack({ items, bg = null, active = true, waiting = false, cartItemIds, onToggleCart, carouselEnabledRef, resetKey, staggerMs = 120 }) {
  // Every hook runs on every render, single item or not. The single-item
  // layout used to return before them, so a row whose stock went from one
  // piece to two (or back) changed the hook order under React, which warned
  // "Expected static flag was missing" on each /showcase load. It returns
  // below, after the hooks; with no track mounted, the main effect finds no
  // element and does nothing.
  const trackRef = useRef(null);
  const targetRef = useRef(0);
  const rafRef = useRef(null);
  const setWidthRef = useRef(0);
  // Filled inside the main effect: arrow clicks animate to the next/prev card
  // center over a duration (slower than wheel inertia). The same path is
  // shared by post-interaction snap so wheel/touch settle into a card too.
  const arrowScrollRef = useRef(null);
  // DOM refs for the side arrows so the scroll/hover visibility logic can
  // toggle classes without re-rendering React.
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);
  const stageReady = useStageReady(stageUrls(bg, items), { active, waiting });
  // A row that sat on screen waiting for its stage (a cold load) brings all
  // of its cards up at once when it is let go, over the backdrop's own fade,
  // so the two land together. A row whose stage came in before it was ever
  // on screen keeps the usual left-to-right reveal as it arrives.
  const waitedOnScreenRef = useRef(false);
  if (!stageReady && active) waitedOnScreenRef.current = true;
  const together = stageReady && waitedOnScreenRef.current;

  const loopedItems = useMemo(() => {
    if (!items.length) return [];
    const out = [];
    for (let c = 0; c < LOOP_COPIES; c++) {
      for (let i = 0; i < items.length; i++) {
        out.push({ item: items[i], copy: c, idx: i });
      }
    }
    return out;
  }, [items]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || !items.length) return;

    const measureSetWidth = () => {
      const cards = el.children;
      if (!cards.length) return 0;
      const styles = window.getComputedStyle(el);
      const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
      const n = Math.min(items.length, cards.length);
      let width = 0;
      for (let i = 0; i < n; i++) {
        width += cards[i].offsetWidth;
        if (i < n - 1) width += gap;
      }
      width += gap;
      return width;
    };

    // Cache per-card offsetLeft/offsetWidth so updateArc never has to read
    // layout in the hot path. Without this, every animation frame triggers
    // a forced sync layout on iOS during momentum scroll, which lands the
    // transform 1 frame behind the compositor and reads as a vertical shake.
    let cardLefts = null;
    let cardWidths = null;
    let cardCount = 0;
    const lastTranslate = []; // per-card: last applied translate (px)
    const lastScale = [];     // per-card: last applied scale (rounded)
    const lastBlur = [];      // per-card: last applied blur px (rounded)
    const lastZ = [];         // per-card: last applied z-index
    const lastHidden = [];    // per-card: last visibility state (boolean)
    let lastCenterIdx = -1;   // which card currently holds the --center class

    // Arc geometry — recomputed in measureCards (on init + resize) instead of
    // every frame. Removing clientWidth/offsetWidth reads from the per-frame
    // hot path eliminates a forced layout each tick, which is what kept
    // iOS momentum scroll stuck below 60fps.
    const SCALE_POINTS = [1.00, 0.60, 0.40, 0.20];
    const EDGE_GAP = 28;
    let cachedHalfView = 0;
    let cachedSideCount = 0;
    let cachedEdgeScale = 0;
    let cachedPositionScale = 1;
    let cachedSlotPx = 0;
    const cachedPos = new Float32Array(8);

    const scaleAt = (k) => {
      if (k <= 0) return 1;
      if (k <= cachedSideCount) {
        const lastIdx = SCALE_POINTS.length - 1;
        const k0 = Math.min(lastIdx, Math.floor(k));
        const k1 = Math.min(lastIdx, k0 + 1);
        const t = k - k0;
        return SCALE_POINTS[k0] * (1 - t) + SCALE_POINTS[k1] * t;
      }
      if (k <= cachedSideCount + 1) {
        return cachedEdgeScale * (1 - (k - cachedSideCount));
      }
      return 0;
    };

    const remeasureArcGeometry = () => {
      const trackWidth = el.clientWidth;
      if (!trackWidth) return;
      cachedHalfView = trackWidth / 2;
      cachedSideCount = trackWidth >= 1024 ? 3 : trackWidth >= 640 ? 2 : 1;
      cachedEdgeScale = SCALE_POINTS[Math.min(cachedSideCount, SCALE_POINTS.length - 1)];

      const W = el.children[0]?.offsetWidth || 1;
      if (W <= 1) return;
      const SIDE_COUNT = cachedSideCount;
      cachedPos[0] = 0;
      for (let k = 1; k <= SIDE_COUNT + 2; k++) {
        cachedPos[k] = cachedPos[k - 1]
          + W * 0.5 * scaleAt(k - 1)
          + W * 0.5 * scaleAt(k)
          + EDGE_GAP;
      }
      const naturalEdge = cachedPos[SIDE_COUNT];
      const targetEdge = cachedHalfView * 0.96;
      cachedPositionScale = naturalEdge > 0 ? Math.min(1, targetEdge / naturalEdge) : 1;
      cachedSlotPx = cachedPos[1] * cachedPositionScale;
    };

    const measureCards = () => {
      const cards = el.children;
      const n = cards.length;
      cardCount = n;
      cardLefts = new Float32Array(n);
      cardWidths = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        cardLefts[i] = cards[i].offsetLeft;
        cardWidths[i] = cards[i].offsetWidth;
      }
      lastTranslate.length = n;
      lastScale.length = n;
      lastBlur.length = n;
      lastZ.length = n;
      lastHidden.length = n;
      for (let i = 0; i < n; i++) {
        lastTranslate[i] = NaN;
        lastScale[i] = NaN;
        lastBlur[i] = NaN;
        lastZ[i] = NaN;
        lastHidden[i] = null;
      }
      // Refresh the arc geometry whenever cards are remeasured — keeps the
      // per-frame updateArc free of layout reads.
      remeasureArcGeometry();
      placeSideArrows();
    };

    // The side arrows ride on the centre card's name line rather than half
    // way up the photos (the owner, 2026-10-16: "move arrows to the bottom
    // of the line"). Read from layout offsets, which the arc's transforms do
    // not touch, and handed to the CSS as --side-arrow-y on the wrap. From
    // the CENTRE card: the track centres its cards vertically, so a piece
    // whose name runs to two lines sits higher than one with a single line,
    // and the line moves with whichever is in front (the arrows ease across,
    // app.css). Read again as the centre changes (updateArc). Before it is
    // known the arrows stay at 50%.
    const placeSideArrows = () => {
      // Not on a touchscreen: its side arrows are hidden (styles/mobile.css),
      // and this layout read landed mid-swipe, as the centre card changed.
      if (isTouchDevice()) return;
      const wrap = el.parentElement;
      const title = el.querySelector(".brand-row__card--center .brand-row__card-title")
        || el.querySelector(".brand-row__card-title");
      if (!wrap || !title) return;
      let y = 0;
      let node = title;
      while (node && node !== wrap) {
        y += node.offsetTop;
        node = node.offsetParent;
      }
      if (node !== wrap) return;
      const cs = getComputedStyle(title);
      const lineH = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.2;
      wrap.style.setProperty("--side-arrow-y", `${Math.round(y + lineH / 2)}px`);
      // And how far out from the middle they stand: just past the centre
      // card's edges, by its own width, never over it or its name. A fixed
      // share of the window (19vw) put them on the card wherever the card is
      // wider than that, 768-1023px wide, over a long name (the owner,
      // 2026-10-25: "fix this sizing issue for titles that are long and when
      // the page is condensed"). The centre card is at full scale and
      // centred, so its untransformed width is its width on screen.
      const card = title.closest(".brand-row__card");
      const arrow = wrap.querySelector(".brand-row__side-arrow");
      if (card && card.offsetWidth) {
        const dx = card.offsetWidth / 2 + SIDE_ARROW_GAP_PX + (arrow?.offsetWidth || 36);
        wrap.style.setProperty("--side-arrow-dx", `${Math.round(dx)}px`);
      }
    };

    const wrapIfNeeded = () => {
      const setW = setWidthRef.current;
      if (!setW) return;
      const lower = setW * 1;
      const upper = setW * (LOOP_COPIES - 2);
      if (el.scrollLeft < lower) {
        el.scrollLeft += setW;
        targetRef.current += setW;
      } else if (el.scrollLeft > upper) {
        el.scrollLeft -= setW;
        targetRef.current -= setW;
      }
    };

    // Coverflow arc — center card largest, edges smaller. Cards are positioned
    // by translating from their natural flex layout to a slot-based offset
    // around the viewport center. Center is always on top via z-index.
    //
    // Per-frame work is kept lean: no layout reads (clientWidth/offsetWidth
    // are baked into the cached geometry by remeasureArcGeometry) and no
    // allocations in the hot path. The only DOM read each frame is
    // el.scrollLeft, which is already a compositor-side value.
    const updateArc = () => {
      const cards = el.children;
      const n = cards.length;
      if (!n || !cachedHalfView) return;

      const scroll = el.scrollLeft;
      const viewCenter = scroll + cachedHalfView;
      const SIDE_COUNT = cachedSideCount;
      const slotPx = cachedSlotPx;
      const pos = cachedPos;
      const positionScale = cachedPositionScale;

      // Use cached layout reads if available; otherwise fall back to live
      // (e.g. very first frame before measureCards has run).
      const useCache = cardLefts && cardLefts.length === n;

      // Track the front-facing (closest-to-center) card so we can tag it with
      // --center. Sold cards in the center slot stay full-opacity; only their
      // dimmed siblings in the side slots fade out.
      let centerIdx = -1;
      let centerAd = Infinity;

      for (let i = 0; i < n; i++) {
        const card = cards[i];
        const oLeft = useCache ? cardLefts[i] : card.offsetLeft;
        const oWidth = useCache ? cardWidths[i] : card.offsetWidth;
        const naturalCenter = oLeft + oWidth / 2;
        const dxPx = naturalCenter - viewCenter;
        const d = slotPx > 0 ? dxPx / slotPx : 0;
        const ad = Math.abs(d);

        if (ad < centerAd) {
          centerAd = ad;
          centerIdx = i;
        }

        const scale = scaleAt(ad);

        // Interpolate visual offset between integer slot positions
        let visualOffset;
        if (ad >= SIDE_COUNT + 2) {
          visualOffset = pos[SIDE_COUNT + 2] * positionScale;
        } else {
          const k0 = Math.floor(ad);
          const k1 = k0 + 1;
          const t = ad - k0;
          visualOffset = (pos[k0] * (1 - t) + pos[k1] * t) * positionScale;
        }
        if (d < 0) visualOffset = -visualOffset;

        const desiredCenter = viewCenter + visualOffset;
        const translateX = desiredCenter - naturalCenter;

        const zIndex = Math.max(0, Math.round((SIDE_COUNT + 2 - ad) * 10));

        // Skip writes whose value didn't change (rounded). Reduces composite
        // work and avoids style invalidations the GPU has to flush.
        const txR = Math.round(translateX * 100) / 100;
        const scR = Math.round(scale * 10000) / 10000;
        const hidden = scale <= 0.005;
        const BLUR_START = 2.5;
        const blurPx = hidden || ad <= BLUR_START ? 0 : Math.min(4, (ad - BLUR_START) * 3);
        // Snap blur to whole pixels: GPU only re-rasterizes the blur shader
        // when the value actually changes, and quantizing limits that to a
        // handful of states across the entire scroll range.
        const blR = Math.round(blurPx);

        if (lastZ[i] !== zIndex) {
          card.style.zIndex = String(zIndex);
          lastZ[i] = zIndex;
        }
        if (lastHidden[i] !== hidden) {
          if (hidden) {
            card.style.opacity = "0";
            card.style.pointerEvents = "none";
            card.style.visibility = "hidden";
          } else {
            // Empty string (not "1") so the CSS staggered reveal animation
            // controls opacity. Setting "1" inline would beat the animation.
            card.style.opacity = "";
            card.style.pointerEvents = "";
            card.style.visibility = "";
          }
          lastHidden[i] = hidden;
        }
        if (lastTranslate[i] !== txR || lastScale[i] !== scR) {
          // translate3d forces an own composite layer per card on iOS so
          // the scale/translate is interpolated by the GPU rather than
          // re-rasterized on each frame.
          card.style.transform = hidden
            ? `translate3d(${txR}px,0,0) scale(0)`
            : `translate3d(${txR}px,0,0) scale(${scR})`;
          lastTranslate[i] = txR;
          lastScale[i] = scR;
        }
        if (lastBlur[i] !== blR) {
          card.style.filter = blR > 0 ? `blur(${blR}px)` : "";
          lastBlur[i] = blR;
        }
      }

      // Move the --center flag to whichever card is now front-facing. Only
      // touch the DOM when the center actually changes.
      if (centerIdx !== lastCenterIdx) {
        if (lastCenterIdx >= 0 && cards[lastCenterIdx]) {
          cards[lastCenterIdx].classList.remove("brand-row__card--center");
        }
        if (centerIdx >= 0 && cards[centerIdx]) {
          cards[centerIdx].classList.add("brand-row__card--center");
        }
        lastCenterIdx = centerIdx;
        placeSideArrows();
      }
    };

    const init = () => {
      const w = measureSetWidth();
      setWidthRef.current = w;
      measureCards();

      // Center the middle item of the anchor copy in the viewport
      const n = items.length;
      const midIdx = Math.floor(n / 2);
      const anchorStart = LOOP_ANCHOR * n;
      const midCard = el.children[anchorStart + midIdx];
      if (midCard) {
        el.scrollLeft = midCard.offsetLeft + midCard.offsetWidth / 2 - el.clientWidth / 2;
      } else {
        el.scrollLeft = w * LOOP_ANCHOR;
      }

      targetRef.current = el.scrollLeft;
      updateArc();
    };

    // Snap animation state — separate rAF from the wheel-inertia tick so
    // arrow clicks and post-interaction settle can use a duration-eased
    // motion (slower, predictable) without fighting the exponential chase.
    let snapAnimRaf = null;
    let snapping = false;

    // Side-arrow scrolling state — true from first wheel/touch through the
    // post-scroll snap. Toggled directly on the DOM nodes via classList so
    // we don't trigger a React re-render on every interaction.
    let scrollingActive = false;
    const setScrolling = (val) => {
      if (scrollingActive === val) return;
      scrollingActive = val;
      const cls = "brand-row__side-arrow--scrolling";
      leftArrowRef.current?.classList.toggle(cls, val);
      rightArrowRef.current?.classList.toggle(cls, val);
    };

    const cancelSnap = () => {
      if (snapAnimRaf) {
        cancelAnimationFrame(snapAnimRaf);
        snapAnimRaf = null;
      }
      snapping = false;
    };

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animateScrollTo = (target, duration, midwayCallback) => {
      cancelSnap();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const start = el.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) < 0.5) {
        // No-op snap — the user already came to rest aligned. Clear the
        // scrolling flag so the side arrows can fade back in. Also fire the
        // midway callback so callers don't have to special-case no-op snaps.
        if (midwayCallback) midwayCallback();
        setScrolling(false);
        return;
      }
      snapping = true;
      let midwayFired = false;
      const t0 = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        el.scrollLeft = start + distance * easeOutCubic(t);
        updateArc();
        // Midway callback fires once when the animation is half-complete.
        // Used by arrow click to start fading the arrows back in while the
        // next card is still landing.
        if (!midwayFired && midwayCallback && t >= 0.5) {
          midwayFired = true;
          midwayCallback();
        }
        if (t < 1) {
          snapAnimRaf = requestAnimationFrame(step);
        } else {
          snapAnimRaf = null;
          snapping = false;
          targetRef.current = el.scrollLeft;
          wrapIfNeeded();
          updateArc();
          // Snap landed — fade the side arrows back in.
          setScrolling(false);
        }
      };
      snapAnimRaf = requestAnimationFrame(step);
    };

    const nearestCardIdx = () => {
      const cards = el.children;
      if (!cards.length) return -1;
      const trackWidth = el.clientWidth;
      const viewCenter = el.scrollLeft + trackWidth / 2;
      const useCache = cardLefts && cardLefts.length === cards.length;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < cards.length; i++) {
        const oLeft = useCache ? cardLefts[i] : cards[i].offsetLeft;
        const oWidth = useCache ? cardWidths[i] : cards[i].offsetWidth;
        const d = Math.abs(oLeft + oWidth / 2 - viewCenter);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    const scrollLeftForCard = (idx) => {
      const cards = el.children;
      if (idx < 0 || idx >= cards.length) return el.scrollLeft;
      const useCache = cardLefts && cardLefts.length === cards.length;
      const oLeft = useCache ? cardLefts[idx] : cards[idx].offsetLeft;
      const oWidth = useCache ? cardWidths[idx] : cards[idx].offsetWidth;
      return oLeft + oWidth / 2 - el.clientWidth / 2;
    };

    const snapToNearest = (duration) => {
      const idx = nearestCardIdx();
      if (idx < 0) return;
      animateScrollTo(scrollLeftForCard(idx), duration);
    };

    // Debounced snap — fires 0.5s after the user stops scrolling/wheeling/
    // touching. Reset on every scroll change so a continuous interaction
    // never trips it; cleared explicitly when the user grabs the carousel
    // again so the in-flight gesture isn't fought.
    const SNAP_IDLE_MS = 150;
    let snapTimer = null;

    const cancelSnapTimer = () => {
      if (snapTimer) {
        clearTimeout(snapTimer);
        snapTimer = null;
      }
    };

    const scheduleSnap = () => {
      cancelSnapTimer();
      snapTimer = setTimeout(() => {
        snapTimer = null;
        snapToNearest(260);
      }, SNAP_IDLE_MS);
    };

    const tick = () => {
      const current = el.scrollLeft;
      const diff = targetRef.current - current;
      if (Math.abs(diff) < 0.3) {
        el.scrollLeft = targetRef.current;
        wrapIfNeeded();
        updateArc();
        rafRef.current = null;
        // Wheel inertia just settled — let the idle-snap timer take over.
        scheduleSnap();
        return;
      }
      el.scrollLeft = current + diff * 0.16;
      wrapIfNeeded();
      updateArc();
      rafRef.current = requestAnimationFrame(tick);
    };

    // Only capture wheel for horizontal carousel when hovering over the track
    let hoveringTrack = false;
    const onPointerEnter = () => {
      hoveringTrack = true;
    };
    const onPointerLeave = () => {
      hoveringTrack = false;
    };

    const onWheel = (e) => {
      if (!hoveringTrack) return; // let page scroll handle it
      if (carouselEnabledRef && !carouselEnabledRef.current) return;
      // A mouse wheel rolled UP moves on through the pieces, down goes back,
      // as the wheel pages the brands (the owner, 2026-10-17: "same for
      // scroll forward on the items for showcase page"). A sideways swipe
      // keeps its own sense.
      const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? -e.deltaY : e.deltaX;
      if (!delta) return;
      e.preventDefault();
      // User took control again — abort any in-flight snap and rebase the
      // chase target to where the carousel actually is, otherwise the stale
      // pre-snap target would yank scroll backwards.
      cancelSnapTimer();
      if (snapping) {
        targetRef.current = el.scrollLeft;
        cancelSnap();
      }
      setScrolling(true);
      targetRef.current += delta;
      if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
    };

    // Native touch scroll fires `scroll` events at irregular times that
    // aren't aligned with paint frames. Running updateArc synchronously on
    // each one applies transforms out-of-phase with the GPU-composited
    // scroll position, which reads as a vertical rumble because every card
    // scales around its center (top/bottom edges wobble on each mismatch).
    // Coalesce to one update per frame to keep arc & scroll in lockstep.
    let scrollRaf = null;
    const onScroll = () => {
      // Any scroll movement (wheel, native touch, programmatic) resets the
      // 1s idle-snap timer. The timer only fires once scrolling has been
      // quiet for the full duration. Skipped while a snap animation is
      // running so the snap's own scrollLeft writes don't keep deferring it.
      if (!snapping) scheduleSnap();
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = null;
        // Don't run wrapIfNeeded during a snap — the snap animates a fixed
        // distance and a wrap-jump mid-flight would derail the easing.
        if (!rafRef.current && !snapping) {
          targetRef.current = el.scrollLeft;
          wrapIfNeeded();
        }
        updateArc();
      });
    };

    // iOS Safari decouples scroll events from the compositor: during a
    // touch fling the cards scroll at 60–120fps but the `scroll` event
    // listener only fires ~10–20Hz, so updateArc lags behind and the
    // scale/translate jumps in steps instead of a smooth arc. Polling
    // scrollLeft every animation frame while a finger is on the track
    // (and through momentum after release) keeps the transforms in
    // lockstep with the actual scroll position.
    let touchPollRaf = null;
    let touching = false;
    let lastPolledScroll = el.scrollLeft;
    let stillFrames = 0;

    const pollScroll = () => {
      const cur = el.scrollLeft;
      if (cur !== lastPolledScroll) {
        lastPolledScroll = cur;
        if (!rafRef.current && !snapping) {
          targetRef.current = cur;
          wrapIfNeeded();
        }
        updateArc();
        stillFrames = 0;
      } else {
        stillFrames++;
      }
      // Keep polling while finger is down OR momentum is still moving
      // the scroll. Stop after ~10 still frames (~165ms) once released.
      if (touching || stillFrames < 10) {
        touchPollRaf = requestAnimationFrame(pollScroll);
      } else {
        touchPollRaf = null;
        // Touch + momentum settled — let the idle-snap timer take it from
        // here so the snap fires 1s after the last interaction.
        if (!touching) scheduleSnap();
      }
    };

    const onTouchStart = () => {
      // User grabbed the carousel — abort any pending snap (timer + animation)
      // before native scrolling takes over.
      cancelSnapTimer();
      cancelSnap();
      setScrolling(true);
      touching = true;
      stillFrames = 0;
      lastPolledScroll = el.scrollLeft;
      if (!touchPollRaf) {
        touchPollRaf = requestAnimationFrame(pollScroll);
      }
    };

    const onTouchEnd = () => {
      touching = false;
      // pollScroll loop continues until momentum settles (still frames check)
    };

    const onResize = () => {
      const prevSet = setWidthRef.current || 1;
      const ratio = el.scrollLeft / prevSet;
      const w = measureSetWidth();
      setWidthRef.current = w;
      measureCards();
      el.scrollLeft = w * ratio;
      targetRef.current = el.scrollLeft;
      updateArc();
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("pointerenter", onPointerEnter);
    el.addEventListener("pointerleave", onPointerLeave);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    window.addEventListener("resize", onResize);
    // The cards reach their final height after the track first lays out (the
    // name sat 18px lower once they had), so the arrows' line is read again
    // whenever the track or its first card changes size.
    const arrowLineObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => placeSideArrows())
      : null;
    if (arrowLineObserver) {
      arrowLineObserver.observe(el);
      if (el.firstElementChild) arrowLineObserver.observe(el.firstElementChild);
    }

    arrowScrollRef.current = (dir) => {
      if (carouselEnabledRef && !carouselEnabledRef.current) return;
      // Guard against double-clicks on the invisible-but-still-interactive
      // arrow during a snap (we deliberately keep pointer-events on so
      // mouseLeave doesn't fire and unhide the opposite arrow).
      if (scrollingActive) return;
      const cards = el.children;
      if (!cards.length) return;
      cancelSnapTimer();
      const cur = nearestCardIdx();
      const target = Math.max(0, Math.min(cards.length - 1, cur + dir));
      if (target === cur) return;
      // Fade arrows out at click, fade back in halfway through the snap so
      // they reappear as the next card is landing. Slower than wheel inertia
      // (~280ms) so the click reads as a deliberate step rather than a
      // hurried snap.
      setScrolling(true);
      animateScrollTo(scrollLeftForCard(target), 460, () => setScrolling(false));
    };

    const initRaf = requestAnimationFrame(init);

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("pointerenter", onPointerEnter);
      el.removeEventListener("pointerleave", onPointerLeave);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("resize", onResize);
      arrowLineObserver?.disconnect();
      cancelAnimationFrame(initRaf);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      if (touchPollRaf) cancelAnimationFrame(touchPollRaf);
      if (snapAnimRaf) cancelAnimationFrame(snapAnimRaf);
      if (snapTimer) clearTimeout(snapTimer);
      arrowScrollRef.current = null;
    };
  }, [loopedItems, resetKey]);

  const onArrowClick = useCallback((dir) => {
    arrowScrollRef.current?.(dir);
  }, []);

  // Single item — no carousel, just center the card
  if (items.length === 1) {
    const item = items[0];
    return (
      <div className={`brand-row__track brand-row__track--single${stageReady ? "" : " brand-row__track--held"}${together ? " brand-row__track--together" : ""}`}>
        <ProductCard
          item={item}
          inCart={cartItemIds.has(item.id)}
          onToggleCart={onToggleCart}
        />
      </div>
    );
  }

  return (
    <div className={`brand-row__track-wrap${stageReady ? "" : " brand-row__track-wrap--held"}${together ? " brand-row__track-wrap--together" : ""}`}>
      <button
        ref={leftArrowRef}
        type="button"
        className="brand-row__side-arrow brand-row__side-arrow--left"
        aria-label="Scroll left"
        onClick={() => onArrowClick(-1)}
        onMouseEnter={() => rightArrowRef.current?.classList.add("brand-row__side-arrow--solo-hidden")}
        onMouseLeave={() => rightArrowRef.current?.classList.remove("brand-row__side-arrow--solo-hidden")}
      >
        <svg width="13" height="27" viewBox="0 0 8 14" fill="none"
          stroke="currentColor" strokeWidth="1.24" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 1L1 7L7 13" />
        </svg>
      </button>
      <button
        ref={rightArrowRef}
        type="button"
        className="brand-row__side-arrow brand-row__side-arrow--right"
        aria-label="Scroll right"
        onClick={() => onArrowClick(1)}
        onMouseEnter={() => leftArrowRef.current?.classList.add("brand-row__side-arrow--solo-hidden")}
        onMouseLeave={() => leftArrowRef.current?.classList.remove("brand-row__side-arrow--solo-hidden")}
      >
        <svg width="13" height="27" viewBox="0 0 8 14" fill="none"
          stroke="currentColor" strokeWidth="1.24" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 1L7 7L1 13" />
        </svg>
      </button>
      <div className="brand-row__track" ref={trackRef}>
      {loopedItems.map(({ item, copy, idx }) => {
        // Stagger left→right starting from the leftmost visible card. Items
        // are arranged center-out, so visible cards span roughly
        // [mid - SIDE_COUNT, mid + SIDE_COUNT] (max SIDE_COUNT=3 on desktop).
        // Subtracting (mid - 3) makes the leftmost desktop-visible card start
        // at 0ms instead of waiting through ~mid×120ms of off-screen delay.
        const mid = Math.floor(items.length / 2);
        const revealStep = Math.max(0, idx - (mid - 3));
        return (
          <ProductCard
            key={`${copy}-${idx}-${item.id}`}
            item={item}
            inCart={cartItemIds.has(item.id)}
            onToggleCart={onToggleCart}
            style={{ animationDelay: `${together ? 0 : revealStep * staggerMs}ms` }}
          />
        );
      })}
      </div>
    </div>
  );
});

// ── Title strip: grab, coast, page ──
// The fade over the strip's right edge: its mask and its padding-right in
// app.css (.brand-row__title-strip).
const STRIP_FADE_PX = 96;
// How far the mouse must move with the button down before a press becomes a
// grab. Under it, the press is a click on the name.
const STRIP_GRAB_PX = 5;

// The scroll position that puts each name at the strip's left edge, and the
// strip's end (where the last names sit clear of the fade).
function stripRestPositions(strip) {
  const s = strip.getBoundingClientRect();
  const max = strip.scrollWidth - strip.clientWidth;
  const out = [...strip.children].map((el) => Math.min(max, Math.max(0, strip.scrollLeft + el.getBoundingClientRect().left - s.left)));
  out.push(max);
  return out;
}

// Moves the strip to the name nearest target (or its end), smoothly. Snapping
// stays off (--free) until the move has settled, so the browser's own snap
// cannot jump in halfway.
function settleStrip(strip, target) {
  const rests = stripRestPositions(strip);
  const left = rests.reduce((best, p) => (Math.abs(p - target) < Math.abs(best - target) ? p : best), rests[0]);
  strip.classList.add("brand-row__title-strip--free");
  strip.scrollTo({ left, behavior: "smooth" });
  clearTimeout(strip.__stripSettle);
  const done = () => {
    clearTimeout(strip.__stripSettle);
    strip.removeEventListener("scrollend", done);
    strip.classList.remove("brand-row__title-strip--free");
  };
  strip.addEventListener("scrollend", done);
  strip.__stripSettle = setTimeout(done, 700);
}


// A name under the right-edge fade is not a jump: pressing it pages the strip
// on, bringing that name to the front, so it and the names after it (about
// three at desktop width) are the ones in full view. Pressing the next faded
// name pages on again. Returns true when it paged.
function pageStripTo(strip, item) {
  const s = strip.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  if (r.right <= s.right - STRIP_FADE_PX + 8) return false;
  settleStrip(strip, strip.scrollLeft + r.left - s.left);
  return true;
}

// Grabbing the names with the mouse drags them along; letting go coasts on
// the pointer's speed and settles on the nearest name. A grab is never a
// click: the click that ends one is swallowed before React sees it, so it
// neither jumps to a brand nor pages the strip. Touch scrolls the strip
// natively and is left alone. The pointer is captured only once the press
// has become a grab, so an ordinary click keeps its own target.
function attachStripDrag(strip) {
  let press = null; // { id, x, scroll }
  let grabbing = false;
  let samples = [];
  // The move and the release are listened for on the window for the length
  // of a press: a grab often leaves the strip within its first few pixels
  // (drag the first name left and the pointer is over the title), and a
  // release missed out there would leave a stale press to turn the next
  // hover into a grab.
  const listen = (on) => {
    const fn = on ? "addEventListener" : "removeEventListener";
    window[fn]("pointermove", onMove, true);
    window[fn]("pointerup", onEnd, true);
    window[fn]("pointercancel", onEnd, true);
  };
  function onMove(e) {
    if (!press || e.pointerId !== press.id) return;
    // The button came up somewhere no event reached us from.
    if (!(e.buttons & 1)) { onEnd(e); return; }
    const dx = e.clientX - press.x;
    if (!grabbing) {
      if (Math.abs(dx) < STRIP_GRAB_PX) return;
      grabbing = true;
      // A grab can start on the folded title (the current brand's twin in the
      // strip lies over it and takes the press): the strip opens on the spot,
      // and the grab measures from wherever that leaves it, so the row follows
      // the pointer from the first pixel.
      if (strip.__onGrabStart?.()) press.scroll = strip.scrollLeft;
      try { strip.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
      clearTimeout(strip.__stripSettle);
      strip.classList.add("brand-row__title-strip--free", "brand-row__title-strip--grabbing");
    }
    strip.scrollLeft = press.scroll - dx;
    samples.push({ t: e.timeStamp, x: e.clientX });
    if (samples.length > 8) samples.shift();
  }
  function onEnd() {
    listen(false);
    if (!press) return;
    const wasGrabbing = grabbing;
    press = null;
    grabbing = false;
    if (!wasGrabbing) return;
    strip.classList.remove("brand-row__title-strip--grabbing");
    strip.__onGrabEnd?.();
    // The click that ends a grab, wherever it lands, comes in this same
    // task: swallow it before anything (React included) sees it.
    const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
    window.addEventListener("click", swallow, true);
    setTimeout(() => window.removeEventListener("click", swallow, true), 0);
    // Coast: the pointer's speed over its last 100ms, carried ~220ms on.
    const last = samples[samples.length - 1];
    const first = samples.find((p) => last.t - p.t <= 100) || last;
    const v = last.t > first.t ? (last.x - first.x) / (last.t - first.t) : 0;
    settleStrip(strip, strip.scrollLeft - v * 220);
  }
  strip.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    if (strip.scrollWidth - strip.clientWidth <= 0) return;
    press = { id: e.pointerId, x: e.clientX, scroll: strip.scrollLeft };
    grabbing = false;
    samples = [{ t: e.timeStamp, x: e.clientX }];
    listen(true);
  });
}

const NO_ITEMS = [];

const BrandRowBackground = memo(forwardRef(function BrandRowBackground({ bg, items = NO_ITEMS, active = true }, blackoutRef) {
  // Shown when its brand's stage is in (useStageReady: this backdrop and the
  // photos of the cards on screen), in the same frame as the cards, and
  // never before the image itself has decoded: CSS `background-image` paints
  // scanline by scanline as bytes arrive, which reads as the backdrop
  // "loading slowly" from the top down, where a decoded image fades in whole.
  // items and active: the brand's products and whether its row has been on
  // screen, as for BrandRowTrack.
  const ready = useStageReady(stageUrls(bg, items), { active });
  const decoded = useSyncExternalStore(subscribeImages, () => bg.type === "video" || imageDecoded(bg.url));
  const shown = ready && decoded;

  const imgStyle = useMemo(() => ({
    backgroundImage: `url(${bg.url})`,
    opacity: shown ? 1 : 0,
    ...(bg.blur ? { filter: `blur(${bg.blur}px)` } : {}),
  }), [bg.url, bg.blur, shown]);

  const vignetteStyle = useMemo(() => {
    const d = bg.darken / 100;
    const v = bg.vignette / 100;
    return {
      background: `linear-gradient(rgba(0,0,0,${d}), rgba(0,0,0,${d})), radial-gradient(ellipse 120% 120% at 50% 50%, transparent ${Math.round(20 - v * 10)}%, rgba(0,0,0,${Math.min(1, 0.15 * v / 0.5)}) 45%, rgba(0,0,0,${Math.min(1, 0.35 * v / 0.5)}) 65%, rgba(0,0,0,${Math.min(1, 0.6 * v / 0.5)}) 85%, rgba(0,0,0,${Math.min(1, 0.8 * v / 0.5)}) 100%)`,
    };
  }, [bg.darken, bg.vignette]);

  return (
    <>
      <div className={`brand-row__bg-image${shown ? " brand-row__bg-image--in" : ""}`} style={imgStyle} />
      <div className="brand-row__bg-vignette" style={vignetteStyle} />
      <div className="brand-row__bg-blackout" ref={blackoutRef} />
    </>
  );
}));

export default function BrandCarousel({ setHeaderLoading, setHeaderCollapsed, holdHeader = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { items: cartItems, addItem: addToCart, removeItem: removeFromCart } = useCart();
  const [data, setData] = useState(() => getCachedItems());
  const [soldData, setSoldData] = useState([]);
  const [dataLoading, setDataLoading] = useState(() => !getCachedItems());
  // Phone width: the title line's pager moves to the count line there.
  const isPhone = useIsMobile();
  const [brandBgs, setBrandBgs] = useState({});
  // Whether the backdrops list has arrived (brandBgs is {} before it, and can
  // still be {} after it). Until then no row knows if it has a backdrop, so
  // the stage gate holds its cards (BrandRowTrack's waiting).
  const [bgsLoaded, setBgsLoaded] = useState(false);
  // The brands the owner hid from the Showcase (site manager's per-brand
  // switch), lowercased, or null while that is not known yet. It arrives with
  // the backdrops, so a warm cache has it at once. The deck is held back until
  // it is known, so a hidden brand never shows and then goes; listLoading
  // keeps the page "loading" meanwhile (an empty deck that is not loading
  // renders nothing at all).
  const [hiddenBrands, setHiddenBrands] = useState(() => getShowcaseHiddenBrands());
  const listLoading = dataLoading || !hiddenBrands;

  // Fetch data. fetchItems handles the TTL cache + in-flight dedupe itself,
  // so a warm cache (filled by the landing-page warmup) resolves the first
  // microtask and the loading bar barely flickers.
  //
  // Mercari-style reveal: we don't wait for any images to load before showing
  // the carousel. The page reveals as soon as the inventory JSON arrives, and
  // each <img> fades in via its own onLoad handler when its bytes finish.
  // Aspect-ratio boxes prevent layout shift while images are pending.
  useEffect(() => {
    let active = true;
    if (!getCachedItems()) setDataLoading(true);
    fetchItems()
      .then((items) => {
        if (active) {
          setData(items);
          setDataLoading(false);
        }
      })
      .catch(() => {
        if (active) setDataLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    fetchBrandBackgrounds().then((map) => {
      setBrandBgs(map);
      // Nothing hidden if the list could not be read: a hiccup must not blank the Showcase.
      setHiddenBrands(getShowcaseHiddenBrands() || new Set());
      setBgsLoaded(true);
    });
  }, []);

  // Sold items load alongside available items but don't gate the loading bar —
  // they only appear at the outer slots of each carousel.
  useEffect(() => {
    let active = true;
    fetchSoldItems()
      .then((items) => {
        if (active) setSoldData(items);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const brandRows = useMemo(() => {
    if (!data || !data.length || !hiddenBrands) return [];

    // Only include items that have a brand assigned via site manager (brand_id set),
    // and not those of a brand the owner hid from the Showcase (its sold pieces
    // follow below, since they only join a brand that has a row).
    const grouped = {};
    for (const item of data) {
      if (!item.brand_id) continue;
      const brand = (item.brand_name || "").toLowerCase().trim();
      if (!brand || hiddenBrands.has(brand)) continue;
      if (!grouped[brand]) grouped[brand] = { available: [], sold: [] };
      grouped[brand].available.push(item);
    }
    // Sold items only appended to brands that already have available stock —
    // we don't surface a brand row that's purely sold-out.
    for (const item of soldData) {
      if (!item.brand_id) continue;
      const brand = (item.brand_name || "").toLowerCase().trim();
      if (!brand || !grouped[brand]) continue;
      grouped[brand].sold.push(item);
    }

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, { available, sold }]) => {
        // Sort each pool by price descending as popularity proxy. Concatenating
        // [available, sold] feeds the center-out arranger so available items
        // claim the inner slots first; sold items spill to the outer edges.
        const sortedAvail = [...available].sort((a, b) => (b.price_usd || 0) - (a.price_usd || 0));
        const sortedSold = [...sold].sort((a, b) => (b.price_usd || 0) - (a.price_usd || 0));
        const sorted = [...sortedAvail, ...sortedSold];
        // Arrange center-out: #1 center, #2 right, #3 left, #4 right, ...
        const n = sorted.length;
        const mid = Math.floor(n / 2);
        const arranged = [];
        let left = mid - 1, right = mid + 1;
        arranged[mid] = sorted[0];
        for (let i = 1; i < n; i++) {
          if (i % 2 === 1 && right < n) {
            arranged[right] = sorted[i];
            right++;
          } else if (left >= 0) {
            arranged[left] = sorted[i];
            left--;
          } else if (right < n) {
            arranged[right] = sorted[i];
            right++;
          }
        }
        return { name, items: arranged, availableCount: sortedAvail.length };
      });
  }, [data, soldData, hiddenBrands]);

  // Whether the title strip (the rest of the deck on the title's line) is
  // fanned out. Closed by default: the page opens on the one brand name, and
  // pressing that name is what reveals the others. One value for every row,
  // like the die, so whichever title is on screen toggles the same strip.
  const [stripOpen, setStripOpen] = useState(false);
  // Hover preview from the title strip: { row, idx } while a name in that
  // row's strip is under the pointer. The row stays where it is (title line,
  // strip and position) and previews the hovered brand's page in it: the
  // screen dips to black and comes up on that brand's backdrop, with its
  // breadcrumb, product count and products, until the pointer leaves the
  // names. A press navigates there, as a dot does.
  const [stripPreview, setStripPreview] = useState(null);
  useEffect(() => {
    if (!stripOpen) setStripPreview(null);
  }, [stripOpen]);
  // The page a row is showing: its own, or the hovered brand's while one of
  // its strip names is under the pointer. Read at render, after displayRows.
  const shownRowFor = (row, rowIndex) =>
    (stripPreview && stripPreview.row === rowIndex && displayRows[stripPreview.idx]) || row;
  // Mirror of stripOpen for closures that are armed long before the strip
  // is touched and read the ref rather than being torn down and re-armed
  // every time it toggles (the idle collapse, the title peek, the grab).
  const stripOpenRef = useRef(false);
  stripOpenRef.current = stripOpen;
  // The page can ask for the header to be held up (it does while the brand
  // index list is over the carousel). Same shape as stripOpenRef: read by the
  // idle collapse below, which is a closure that must not be re-armed.
  const holdHeaderRef = useRef(false);
  holdHeaderRef.current = holdHeader;
  // ...and the moment a hold begins, whatever the idle timer had already done
  // is undone, so the header is never found missing under a list it should be
  // sitting above.
  useEffect(() => {
    if (!holdHeader) return;
    document.body.classList.remove("brands-page-idle");
    if (setHeaderCollapsed) setHeaderCollapsed(false);
  }, [holdHeader, setHeaderCollapsed]);
  // Each row's strip node, by row index, so the open-state wheel router can
  // find the one that is actually on screen.
  const stripNodesRef = useRef([]);

  // Rows in display order. The die that used to shuffle them is gone, so
  // this is the alphabetical deck as it stands; the name stays because
  // everything index-based below (render, dots, goToBrand, wheel paging)
  // reads it.
  const displayRows = brandRows;

  // Preload the whole deck as soon as it and the backdrops list are known,
  // in the order it is likely to be wanted: every backdrop, then each brand's
  // stage photos (the cards on screen when it comes up), nearest brands first
  // around the ring from the one on screen, then the rest of every brand's
  // photos, for scrolling a row (not on touch devices, where they would cost
  // data for cards that may never be reached). The brand on screen and its
  // neighbours go ahead of all of it (the effect after nearestBrand), and a
  // row on screen whose stage is not in yet (a hovered name in the title
  // strip, say) jumps the queue itself (useStageReady).
  // A phone does not queue the whole deck. It moves a brand at a time (a
  // swipe, the edge arrows, Up next) and has no title strip to jump with
  // (handleTitleClick), and the brand on screen and its neighbours load ahead
  // of everything anyway (the effect after nearestBrand); a pick from the
  // menu's brand list loads that brand's stage when it arrives. Up front, the
  // whole deck was every backdrop and a stage of photos for every brand
  // before the first swipe. Desktop and tablet are unchanged.
  const deckPreload = !isPhone;
  useEffect(() => {
    const n = displayRows.length;
    if (!n || !bgsLoaded || !deckPreload) return;
    const from = Math.min(Math.max(pageRef.current, 0), n - 1);
    const order = displayRows
      .map((r, i) => ({ r, d: ringDistance(from, i, n) }))
      .sort((a, b) => a.d - b.d)
      .map(({ r }) => r);
    preloadImages(order.map((r) => backdropUrl(brandBgs[r.name])), { keep: true });
    preloadImages(order.flatMap((r) => stagePhotoUrls(r.items)), { keep: true });
    if (!isTouchDevice()) preloadImages(order.flatMap((r) => r.items.map(getImageUrl)));
  }, [displayRows, brandBgs, bgsLoaded, deckPreload]);
  // Leaving the showcase drops what is still queued, so it does not compete
  // with the next page's own images. Coming back queues whatever is not in.
  useEffect(() => () => clearPreloadQueue(), []);

  const [snappedBrand, setSnappedBrand] = useState(0);
  // Nearest brand index — always set (unlike snappedBrand which goes null
  // mid-transition). Drives which brand backgrounds are actually mounted.
  const [nearestBrand, setNearestBrand] = useState(0);
  // The brand on screen and its two neighbours (a slide or a wrap away) load
  // ahead of the rest of the deck.
  useEffect(() => {
    const n = displayRows.length;
    if (!n || !bgsLoaded) return;
    const at = Math.min(Math.max(nearestBrand, 0), n - 1);
    const near = [...new Set([at, (at + 1) % n, (at - 1 + n) % n])];
    preloadImages(
      near.flatMap((i) => stageUrls(brandBgs[displayRows[i].name], displayRows[i].items)),
      { first: true, keep: true },
    );
  }, [nearestBrand, displayRows, brandBgs, bgsLoaded]);
  const [atFooter, setAtFooter] = useState(false);
  const [mouseActive, setMouseActive] = useState(false);
  // Active index for the right-edge dot indicator (footer = brandRows.length).
  // Locked to the target on goToBrand so a multi-step jump shows a single
  // clean handoff — old dot fades back to gray, new dot fades to white via
  // the CSS transition — instead of flickering through every dot in between
  // as nearestBrand cycles. Synced back from natural scroll via the effect
  // below.
  const [dotActiveIdx, setDotActiveIdx] = useState(0);

  useEffect(() => {
    const mouseTimer = { current: null };
    const onMove = () => {
      setMouseActive(true);
      clearTimeout(mouseTimer.current);
      mouseTimer.current = setTimeout(() => setMouseActive(false), 1200);
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      clearTimeout(mouseTimer.current);
    };
  }, []);

  // Header availability (desktop): the header stays fully up on EVERY brand
  // panel — paging, wheeling and moving over the artwork no longer tuck it
  // away. It collapses only after HEADER_IDLE_MS of genuine inactivity, and
  // any activity brings it straight back. Each panel reserves its 90px of
  // clearance while it is shown (the brands-page:not(.brands-page-idle) rule
  // in app.css), so a brand title never renders under the nav.
  useEffect(() => {
    // Not on a touchscreen or at phone width: the header never tucks away
    // there (Header.jsx keeps it up on phones), so the idle model, which also
    // drops each panel's header clearance, must not run either.
    const isTouch = isTouchDevice();
    if (isTouch || isMobileViewport()) return;

    // The body class is the source of truth for "is the header tucked away",
    // so this stays correct even when another effect collapses the header
    // (the immersive location.state.hideHeader arrival), and it no-ops on the
    // mousemove stream instead of setting state on every frame.
    const setCollapsed = (collapsed) => {
      // Never tuck the header away while the title strip is out: the glass
      // freezes the page but the header has to stay in view above it, and
      // the wheel router swallows wheel events before the activity listeners
      // below can see them, so idleness would otherwise be misread.
      if (collapsed && (stripOpenRef.current || holdHeaderRef.current)) return;
      if (document.body.classList.contains("brands-page-idle") === collapsed) return;
      document.body.classList.toggle("brands-page-idle", collapsed);
      if (setHeaderCollapsed) setHeaderCollapsed(collapsed);
    };

    let idleId = null;
    const kick = () => {
      clearTimeout(idleId);
      idleId = setTimeout(() => setCollapsed(true), HEADER_IDLE_MS);
    };
    const onActivity = () => {
      setCollapsed(false);
      kick();
    };

    // Arm the countdown WITHOUT revealing: an immersive arrival collapses the
    // header on the next frame and must not be overridden from here.
    kick();

    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("wheel", onActivity, { passive: true });
    window.addEventListener("pointerdown", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);
    return () => {
      clearTimeout(idleId);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("wheel", onActivity);
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      document.body.classList.remove("brands-page-idle");
    };
  }, [setHeaderCollapsed]);

  // Carousel horizontal scrolling is only allowed when the user is fully
  // settled on a brand (not scrolling the page, snap completed).
  const carouselEnabledRef = useRef(true);
  // True only for the 400ms of a seamless wrap (wrapToBrand), so the wheel
  // handler leaves the scroll position alone while the wrap owns it.
  const wrapActiveRef = useRef(false);

  // Show header loading bar only while fetching data — let images stream in lazily
  useEffect(() => {
    if (setHeaderLoading) {
      setHeaderLoading(listLoading);
    }
  }, [listLoading, setHeaderLoading]);



  // Reveal rows when they scroll into view
  const rowRefs = useRef([]);
  const carouselRef = useRef(null);
  const [visibleRows, setVisibleRows] = useState(new Set());

  // The face the brand titles are drawn in, for titleInkEm: read off a title
  // once the deck is up, and again as each web font lands (the title's face
  // arrives after the first paint, and the trial faces swap in later still).
  const [titleInk, setTitleInk] = useState({ font: null, v: 0 });
  useEffect(() => {
    if (listLoading || !brandRows.length) return undefined;
    const read = () => {
      const el = carouselRef.current?.querySelector(".brand-row__title");
      if (!el) return;
      const s = getComputedStyle(el);
      titleInkCache.clear();
      setTitleInk((prev) => ({ font: `${s.fontStyle} ${s.fontWeight} 200px ${s.fontFamily}`, v: prev.v + 1 }));
    };
    read();
    const fonts = document.fonts;
    let live = true;
    fonts?.ready.then(() => { if (live) read(); });
    fonts?.addEventListener?.("loadingdone", read);
    return () => {
      live = false;
      fonts?.removeEventListener?.("loadingdone", read);
    };
  }, [listLoading, brandRows.length]);

  useEffect(() => {
    if (listLoading || !brandRows.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.rowIndex);
            setVisibleRows((prev) => new Set([...prev, idx]));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    rowRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [listLoading, brandRows.length]);

  // Refs for each brand's blackout div — used for scroll-driven opacity
  const blackoutRefs = useRef([]);
  // Track the last emitted brand-page so we only fire `brand-page-changed`
  // on actual snap transitions, not on every wheel/touch frame.
  const lastEmittedPageRef = useRef(0);

  // Continuously update blackout opacity using container scroll.
  // Horizontal layout: sections are 100vw panels in a row, so all paging
  // math reads scrollLeft / clientWidth.
  const updateBlackouts = useCallback((el) => {
    const sectionW = el.clientWidth;
    if (!sectionW) return;
    const scroll = el.scrollLeft;

    // Header visibility while paging. Desktop: it belongs to the activity /
    // idle model above, and paging never tucks it away (every panel reserves
    // its clearance). Touch: the header stays up on every brand. It used to
    // collapse past the first one, and with no pointer to wake it a phone
    // was left with no menu, search or cart, and no way off the page; every
    // panel now reserves the clearance on touch too (styles/mobile.css).

    // Brand positions: brand i starts at i * sectionW
    for (let i = 0; i < blackoutRefs.current.length; i++) {
      const div = blackoutRefs.current[i];
      if (!div) continue;
      const sectionStart = i * sectionW;
      const dist = Math.abs(scroll - sectionStart) / sectionW;
      const fade = Math.min(1, 0.15 + Math.pow(dist, 0.5) * 0.85);
      div.style.opacity = String(fade);
    }
    const nearest = Math.round(scroll / sectionW);
    const nearDist = Math.abs(scroll - nearest * sectionW) / sectionW;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const hasFooter = el.scrollWidth > sectionW + 10;
    const onFooter = hasFooter && maxScroll > 0 && (maxScroll - scroll) < sectionW * 0.2;
    setAtFooter(onFooter);
    setNearestBrand(onFooter ? blackoutRefs.current.length - 1 : nearest);
    // Mirror onFooter to a body class so CSS can scope footer-only overrides
    // (e.g. suppress the last brand-row's down nav-zone glass strip while
    // viewing the footer so its bottom glow doesn't bleed in).
    document.body.classList.toggle("brands-on-footer", onFooter);

    // Fire a one-shot custom event when the snap-page actually changes (i.e.
    // the user crossed into a new brand). The header listens for this on
    // mobile to dismiss the nav dropdown only when the user navigates to a
    // new page, not on every drag frame.
    const currentPage = onFooter ? blackoutRefs.current.length : nearest;
    if (currentPage !== lastEmittedPageRef.current && nearDist < 0.15) {
      lastEmittedPageRef.current = currentPage;
      document.dispatchEvent(new CustomEvent("brand-page-changed", {
        detail: { page: currentPage, onFooter },
      }));
    }

    if (onFooter) {
      setSnappedBrand(null);
    } else if (nearDist < 0.15) {
      setSnappedBrand(nearest);
    } else if (!isMobileViewport()) {
      // A phone keeps the brand it is leaving until the next one lands. It
      // has no pager, edge strips or title strip to put away mid-swipe, and
      // clearing it re-rendered the deck twice a swipe and flipped the rows'
      // --has-bg styles (the rule, the count) off and on while it moved.
      setSnappedBrand(null);
    }
  }, [setHeaderCollapsed]);

  // A backdrop mounts only near the current brand (ringDistance, in the rows
  // below), so after a jump of more than one brand the new panel's backdrop,
  // blackout and all, arrives in the commit that moves nearestBrand. Left
  // alone, that blackout keeps its default full opacity and the page shows
  // black over a backdrop that is there. So the blackouts are set again as
  // that commit lands, before it paints. jumpToBrand's own rAF pass covers
  // this only when the commit comes first: a jump made from a hover (the
  // showcase list's previews) renders at continuous priority, after that
  // frame.
  useLayoutEffect(() => {
    const el = carouselRef.current;
    if (el) updateBlackouts(el);
  }, [nearestBrand, updateBlackouts]);

  // Backgrounds fetch asynchronously, so the blackout refs don't exist during
  // the first updateBlackouts call on mount. Without this, the overlays stay
  // at their default (fully opaque black) until the user scrolls — visible as
  // the first row (balenciaga) appearing with no background until interaction.
  useEffect(() => {
    if (!Object.keys(brandBgs).length) return;
    const el = carouselRef.current;
    if (!el) return;
    updateBlackouts(el);
  }, [brandBgs, updateBlackouts]);

  // Full-page snap: overflow is hidden on desktop and we manage scrollTop
  // directly via the wheel handler. On touch devices the CSS switches the
  // container to native scroll + scroll-snap and we just listen to scroll
  // events to keep blackouts and pageRef in sync.
  const pageRef = useRef(0);

  const goToBrand = useCallback((page) => {
    const el = carouselRef.current;
    if (!el || !displayRows.length) return;
    if (!carouselEnabledRef.current) return;
    // Page displayRows.length is the half-viewport footer; its target is the
    // bottom of the scrollable content (shows last brand's lower half + footer).
    const maxPage = displayRows.length;
    const clamped = Math.max(0, Math.min(maxPage, page));
    if (clamped === pageRef.current) return;
    pageRef.current = clamped;
    setDotActiveIdx(clamped);
    carouselEnabledRef.current = false;
    const target = clamped === displayRows.length
      ? Math.max(0, el.scrollWidth - el.clientWidth)
      : clamped * el.clientWidth;

    const isTouch = isTouchDevice();

    // Mobile: rAF animation matching the swipe feel. CSS `scroll-snap-type:
    // mandatory` short-circuits smooth scrollTo on iOS, so we disable snap for
    // the duration of the programmatic animation and restore it after.
    if (isTouch) {
      const startTouch = el.scrollLeft;
      const distanceTouch = target - startTouch;
      if (Math.abs(distanceTouch) <= 2) {
        el.scrollLeft = target;
        updateBlackouts(el);
        carouselEnabledRef.current = true;
        return;
      }
      const prevSnap = el.style.scrollSnapType;
      el.style.scrollSnapType = "none";
      const durationTouch = 550;
      const t0Touch = performance.now();
      const easeTouch = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic — swipe-like decel
      const stepTouch = (now) => {
        const t = Math.min(1, (now - t0Touch) / durationTouch);
        el.scrollLeft = startTouch + distanceTouch * easeTouch(t);
        updateBlackouts(el);
        if (t < 1) {
          requestAnimationFrame(stepTouch);
        } else {
          el.style.scrollSnapType = prevSnap;
          carouselEnabledRef.current = true;
        }
      };
      requestAnimationFrame(stepTouch);
      return;
    }

    // Desktop: JS rAF animation matches the wheel handler's slideTo exactly.
    const start = el.scrollLeft;
    const distance = target - start;
    if (Math.abs(distance) <= 2) {
      el.scrollLeft = target;
      updateBlackouts(el);
      carouselEnabledRef.current = true;
      return;
    }
    const duration = 400;
    const t0 = performance.now();
    const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    el.style.overflowX = "scroll";
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      el.scrollLeft = start + distance * ease(t);
      updateBlackouts(el);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        el.style.overflowX = "hidden";
        carouselEnabledRef.current = true;
      }
    };
    requestAnimationFrame(step);
  }, [displayRows.length, updateBlackouts]);

  // Arrival landing: straight onto a brand with no slide, for a visitor who
  // comes to /brand already holding one (see the channel and state effects
  // below). goToBrand's glide is right for a move within the page; on arrival
  // it is a fly-through of every brand in between, on a page still fading in.
  const jumpToBrand = useCallback((page) => {
    const el = carouselRef.current;
    if (!el || !displayRows.length) return;
    const clamped = Math.max(0, Math.min(displayRows.length - 1, page));
    pageRef.current = clamped;
    setDotActiveIdx(clamped);
    const isTouch = isTouchDevice();
    // The same dance as the wheel effect's starting position: desktop keeps
    // the carousel at overflow hidden, which is opened for the one write.
    if (!isTouch) el.style.overflowX = "scroll";
    el.scrollLeft = clamped * el.clientWidth;
    if (!isTouch) el.style.overflowX = "hidden";
    updateBlackouts(el);
    // Once more after React's commit, before the frame paints. Only the
    // current panel and its neighbours mount a background, so a jump further
    // than one brand lands on a panel whose background — and its blackout —
    // mounts only in the commit that follows; the pass above ran before it
    // existed. Left unset, a blackout is fully opaque, and the page came up
    // black over a backdrop that was in fact there. A slide never meets this:
    // it runs updateBlackouts every frame on the way.
    requestAnimationFrame(() => updateBlackouts(el));
  }, [displayRows.length, updateBlackouts]);

  // Marks a brand's panel revealed at once, without .brand-row's 0.4s fade-in,
  // for landing on it in place when it has never been on screen. Hovering its
  // name in the strip normally reveals it already; this covers a pick made
  // without hovering (the keyboard).
  const revealRow = useCallback((idx) => {
    const el = rowRefs.current[idx];
    if (!el || el.classList.contains("brand-row--visible")) return;
    el.style.transition = "none";
    flushSync(() => setVisibleRows((prev) => (prev.has(idx) ? prev : new Set([...prev, idx]))));
    void el.offsetWidth; // commit the untransitioned opacity before restoring
    el.style.transition = "";
  }, []);

  // The title strip — every OTHER brand on the title's own line — scrolls
  // sideways under the wheel. A native listener via callback ref, not React's
  // onWheel: the carousel pages brands from a wheel listener on its root, and
  // that fires while the event bubbles up through the DOM, long before
  // React's root ever sees it. Only a native listener on the strip itself
  // runs first and can stop it. It always consumes the event once the strip
  // can scroll at all — even at either end — so wheeling over the names can
  // never turn into a page change of the deck underneath. The listener lives
  // and dies with its node; each row mounts its own strip.
  const attachStripWheel = useCallback((node) => {
    if (!node) return;
    stripNodesRef.current[Number(node.dataset.rowIndex)] = node;
    if (node.__stripWheel) return;
    const onWheel = (e) => {
      // Sideways only (a trackpad swipe along the names). An up/down scroll
      // is left to bubble to the carousel, which pages the deck, so a scroll
      // switches the title and the screen together even with the names out.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (node.scrollWidth - node.clientWidth <= 0) return;
      e.preventDefault();
      e.stopPropagation();
      node.scrollLeft += e.deltaX;
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    node.__stripWheel = onWheel;
    // The left-edge fog (app.css, .brand-row__title-strip) grows with the
    // distance scrolled, up to the right edge's width: none at rest, so the
    // current brand, first in the strip, sits crisp in the title's place
    // until the row actually moves, then fades as it goes back.
    //
    // The fog is a mask, and a mask that changes is repainted — over the
    // frosted layer under the row, on every frame of a grab, which is what
    // made dragging feel sticky. So it is written at most once a frame, in
    // whole pixels, and only when it changes: past the first 96px of travel
    // it stops changing, and the rest of the drag is a plain scroll under a
    // mask that holds still.
    let fogFrame = 0;
    let fogLast = -1;
    const onScroll = () => {
      if (fogFrame) return;
      fogFrame = requestAnimationFrame(() => {
        fogFrame = 0;
        const px = Math.round(Math.min(STRIP_FADE_PX, node.scrollLeft));
        if (px === fogLast) return;
        fogLast = px;
        node.style.setProperty("--strip-fade-l", `${px}px`);
      });
    };
    node.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    // A grab that starts on the folded title opens the strip at once, with no
    // fade: the current brand has to be there to move from the first pixel,
    // and the title under it has to be gone. --instant holds that until the
    // grab is let go (app.css). Added after the commit, because React writes
    // the row's className as the strip opens.
    node.__onGrabStart = () => {
      // No strip at phone width (handleTitleClick), even under a mouse.
      if (stripOpenRef.current || isMobileViewport()) return false;
      flushSync(() => setStripOpen(true));
      node.closest(".brand-row__title-row")?.classList.add("brand-row__title-row--instant");
      return true;
    };
    node.__onGrabEnd = () => {
      node.closest(".brand-row__title-row")?.classList.remove("brand-row__title-row--instant");
    };
    attachStripDrag(node);
  }, []);

  // Brand title tap fans the rest of the deck out along the title's line and
  // folds it away again. This replaced the old tap behaviour (home from the
  // first brand, snap-to-top further down): HOME is in the header and the
  // strip itself is now the way around the deck.
  // Not on a phone: the owner turned the strip off there as too confusing
  // ("disable the brand scrolling on mobile"). A phone moves through the deck
  // by swipe, the edge arrows and Up next, and jumps from the menu's brand
  // list; the title is only a title.
  // Only a touchscreen's tap now: a pointer opens the strip by hovering the
  // title (hoverOpenStrip), and its press does nothing.
  const handleTitleClick = useCallback(() => {
    if (isMobileViewport()) return;
    if (hasHoverPointer()) return;
    setStripOpen((open) => !open);
  }, []);

  // A window narrowed to phone width folds a strip left open.
  useEffect(() => {
    if (isPhone) setStripOpen(false);
  }, [isPhone]);

  // Every opening starts from the current brand. The strip's first name is
  // laid over the title, and the title is hidden while the strip is out, so a
  // strip left scrolled from last time would open with the title missing. A
  // layout effect, so a grab that opens the strip (flushSync, above) finds it
  // already reset before it starts measuring.
  // Where the lit name is in the open strip, as an offset from the current
  // brand around the ring: 0 is the current brand, 1 the next, -1 the one
  // before. Set by the wheel and by hovering a name; back to 0 on opening.
  const stripPosRef = useRef(0);
  // How many brands have been brought round to the front of the open strip
  // by scrolling back. The strip holds each brand exactly once, in ring
  // order, starting that many before the current one; at 0 it starts at the
  // current brand, over the title.
  const [stripBack, setStripBack] = useState({ row: -1, back: 0 });
  const stripBackRef = useRef(0);
  // Where each name was just before the strip last turned (by brand), so the
  // turn can be animated rather than jump (the layout effect below).
  const stripFlipRef = useRef(null);
  useLayoutEffect(() => {
    if (!stripOpen) return;
    for (const node of stripNodesRef.current) if (node) node.scrollLeft = 0;
    stripPosRef.current = 0;
    stripBackRef.current = 0;
    setStripBack((s) => (s.back === 0 ? s : { row: -1, back: 0 }));
  }, [stripOpen]);

  // ── The wheel walks the open strip ──
  // With the names out, a scroll steps through them instead of paging the
  // deck, wherever the pointer is (the page-level listener below). Each step
  // lights the next name along, or the one before, and previews its page
  // behind, as hovering it does. Scrolling back from the current brand turns
  // the strip: the brand before it comes round from the end to the front, in
  // from behind the left edge, and takes the title's place (stripBack). Every
  // brand is in the strip exactly once. Scrolling on lights names along the
  // row. It stops after a full lap either way. Pressing the lit name goes
  // there, as ever. A mouse's notch is one step; a trackpad's stream of small
  // deltas is summed into a step per 80px, so a swipe runs along the names.
  const stripWheelRef = useRef({ acc: 0, last: 0 });
  const stepStripRef = useRef(null);
  stepStripRef.current = (e) => {
    const row = pageRef.current;
    const n = displayRows.length;
    const strip = stripNodesRef.current[row];
    if (!strip || n < 2) return;
    const d = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    if (!d) return;
    const w = stripWheelRef.current;
    const now = performance.now();
    if (now - w.last > 180) w.acc = 0;
    w.last = now;
    let dir = 0;
    if (e.deltaMode !== 0 || Math.abs(d) >= 50) {
      dir = Math.sign(d);
    } else {
      w.acc += d;
      if (Math.abs(w.acc) >= 80) {
        dir = Math.sign(w.acc);
        w.acc = 0;
      }
    }
    if (!dir) return;
    const k = stripPosRef.current;
    const nextK = Math.max(-(n - 1), Math.min(n - 1, k + dir));
    if (nextK === k) return;
    stripPosRef.current = nextK;
    const idx = (((row + nextK) % n) + n) % n;
    setStripPreview(nextK === 0 ? null : { row, idx, k: nextK });
    // Reveal its panel now, so pressing it lands in place (the strip's hover
    // does the same).
    if (nextK !== 0) setVisibleRows((prev) => (prev.has(idx) ? prev : new Set([...prev, idx])));
    // At or behind the current brand, the lit name is the strip's first, over
    // the title: turn the strip if that puts a different brand first. Where
    // every name is now is recorded first, for the animation after the turn.
    const back = Math.max(0, -nextK);
    if (back !== stripBackRef.current) {
      stripFlipRef.current = {
        strip,
        lefts: new Map([...strip.children].map((el) => [el.dataset.brand, el.getBoundingClientRect().left])),
      };
      stripBackRef.current = back;
      setStripBack({ row, back });
      return;
    }
    if (nextK <= 0) {
      if (strip.scrollLeft) settleStrip(strip, 0);
      return;
    }
    // Ahead of it, the current brand holds the title's place and the lit name
    // moves along the row, the strip paging on to keep it clear of the right
    // edge's fade, with the name before it at the left edge, in the fog.
    const s = strip.getBoundingClientRect();
    const item = strip.querySelector(`[data-k="${nextK}"]`);
    if (item && item.getBoundingClientRect().right > s.right - STRIP_FADE_PX) {
      const lead = strip.querySelector(`[data-k="${nextK - 1}"]`);
      settleStrip(strip, lead ? strip.scrollLeft + lead.getBoundingClientRect().left - s.left : 0);
    } else if (nextK === 1 && strip.scrollLeft) {
      settleStrip(strip, 0);
    }
  };

  // The turn itself. Every name slides from where it was to where it is now,
  // except the ones that went round: a brand brought from the end to the
  // front comes in from behind the left edge, and one sent from the front to
  // the end fades in there. Before paint, so nothing jumps first.
  useLayoutEffect(() => {
    const flip = stripFlipRef.current;
    if (!flip) return;
    stripFlipRef.current = null;
    const { strip, lefts } = flip;
    strip.scrollLeft = 0;
    const width = strip.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
    const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
    for (const el of strip.children) {
      const was = lefts.get(el.dataset.brand);
      if (was == null) continue;
      const r = el.getBoundingClientRect();
      const dx = was - r.left;
      if (Math.abs(dx) < 0.5) continue;
      if (Math.abs(dx) > width / 2) {
        if (el === strip.firstElementChild) {
          el.animate(
            [{ transform: `translateX(${-(r.width + gap)}px)`, opacity: 0 }, { transform: "none", opacity: 1 }],
            { duration: 380, easing: EASE },
          );
        } else {
          el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 380, easing: "ease" });
        }
      } else {
        el.animate([{ transform: `translateX(${dx}px)` }, { transform: "none" }], { duration: 380, easing: EASE });
      }
    }
  }, [stripBack]);

  // While the names are out, the wheel walks them wherever the pointer is on
  // the page, not only over the carousel. Caught at the document, so it is
  // seen first, and stopped there, so nothing under it pages the deck or
  // scrolls the page as well. A sideways swipe over the names themselves is
  // let through: that scrolls them freely (attachStripWheel), as a drag does.
  // Clicking off folds the strip, which ends it.
  useEffect(() => {
    if (!stripOpen) return undefined;
    const onWheel = (e) => {
      const sideways = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (sideways && e.target.closest?.(".brand-row__title-strip")) return;
      e.preventDefault();
      e.stopPropagation();
      stepStripRef.current?.(e);
    };
    document.addEventListener("wheel", onWheel, { capture: true, passive: false });
    return () => document.removeEventListener("wheel", onWheel, { capture: true });
  }, [stripOpen]);

  // Once a fold has faded out, the strip goes back to its resting order and
  // place — the current brand first, over the title — so the title peek's
  // next name sits where it should.
  useEffect(() => {
    if (stripOpen) return undefined;
    const t = setTimeout(() => {
      for (const node of stripNodesRef.current) if (node) node.scrollLeft = 0;
      stripBackRef.current = 0;
      setStripBack((s) => (s.back === 0 ? s : { row: -1, back: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [stripOpen]);

  // Leaving the tab or the window folds the strip and drops any preview, so
  // coming back finds the brand's own page at rest — not the strip left open
  // and scrolled with the current name out of sight, or a hover preview still
  // up because the pointer left without a mouseleave.
  useEffect(() => {
    const reset = () => {
      setStripPreview(null);
      setStripOpen(false);
    };
    const onVisibility = () => {
      if (document.hidden) reset();
    };
    window.addEventListener("blur", reset);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("blur", reset);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // While the strip is open, scrolling still pages the deck: the brand, its
  // screen and its title switch together, and the strip stays out on the new
  // brand (names in ring order from it). The page used to freeze here, every
  // wheel event anywhere scrolling the names and nothing else; the user asked
  // for a scroll to switch titles and screens instead. Sideways swipes over
  // the names still move along them (attachStripWheel).
  useEffect(() => {
    if (!stripOpen) return undefined;
    // The header comes back up the moment the strip opens, whatever the idle
    // timer had done to it, and setCollapsed above holds it there.
    document.body.classList.remove("brands-page-idle");
    if (setHeaderCollapsed) setHeaderCollapsed(false);
    // Keys: left/right walk the names, and anything that would scroll the
    // page (arrows, PageUp/Down, Space, Home, End) is swallowed. Escape folds
    // the strip.
    const STOP = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "]);
    const onKey = (e) => {
      if (e.key === "Escape") {
        setStripOpen(false);
        return;
      }
      const node = stripNodesRef.current[pageRef.current];
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        if (node) node.scrollLeft += e.key === "ArrowRight" ? 160 : -160;
        return;
      }
      if (STOP.has(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () => {
      document.removeEventListener("keydown", onKey, { capture: true });
    };
  }, [stripOpen, setHeaderCollapsed]);

  // Sync the dot indicator from natural scrolling. Skip while a goToBrand
  // travel is mid-flight (carouselEnabledRef === false) so intermediate
  // brands don't flicker the indicator before the click target settles.
  useEffect(() => {
    if (!carouselEnabledRef.current) return;
    setDotActiveIdx(atFooter ? displayRows.length : nearestBrand);
  }, [nearestBrand, atFooter, displayRows.length]);

  // Honor a navigation request from the header dropdown (or any other caller)
  // that asked to land on a specific brand: find its row and run goToBrand,
  // which uses the same rAF transition as inter-brand snap navigation. The
  // navigated state is consumed once so refresh/back doesn't re-trigger it.
  // Arrivals that ask for an immersive landing (e.g. the product page's brand
  // link) hide the site header immediately instead of waiting for the 8s idle
  // timer. Applied on the next frame so it wins over the mount-time
  // updateBlackouts call, which would un-collapse at scrollTop 0.
  const consumedHideHeaderRef = useRef(false);
  useEffect(() => {
    if (consumedHideHeaderRef.current) return;
    if (!location.state?.hideHeader) return;
    consumedHideHeaderRef.current = true;
    // Not on touch or at phone width: the header always stays up there, and
    // on touch nothing would bring it back (the idle model is pointer-driven),
    // which would strand a phone with no navigation.
    if (isTouchDevice() || isMobileViewport()) return;
    const id = requestAnimationFrame(() => {
      document.body.classList.add("brands-page-idle");
      if (setHeaderCollapsed) setHeaderCollapsed(true);
    });
    return () => cancelAnimationFrame(id);
  }, [location.state, setHeaderCollapsed]);

  // Guarded per NAVIGATION, not once per mount. The old one-shot boolean was
  // set on the first target and never cleared, so picking a second brand while
  // already on /brand (the sidebar BRANDS list does exactly that) changed
  // location.state, re-ran this effect, and was ignored. Every navigate()
  // creates a new state object, so remembering the object already acted on
  // skips only the re-runs caused by displayRows or goToBrand changing before
  // the replace() below has cleared the state — the double-fire the boolean
  // was really there to stop — and lets the same brand be picked twice.
  const consumedTargetStateRef = useRef(null);
  // The router state the carousel mounted with: a target in it is an arrival
  // (a product page's brand link), and lands directly rather than sliding.
  const arrivalStateRef = useRef(location.state);
  useEffect(() => {
    if (!displayRows.length) return;
    const state = location.state;
    const target = state?.targetBrand;
    if (!target) return;
    if (consumedTargetStateRef.current === state) return;
    const wanted = String(target).toLowerCase().trim();
    const idx = displayRows.findIndex((r) => r.name.toLowerCase().trim() === wanted);
    if (idx < 0) return;
    consumedTargetStateRef.current = state;
    // Already on the wanted brand — a fresh arrival lands on page 0, and a
    // pick from the sidebar can name the page being viewed — so there is
    // nothing to scroll; just drop the state so back/refresh cannot replay it.
    // (The old check was idx === 0, which assumed the carousel was at the
    // start: true on arrival, wrong once the visitor has paged along.)
    if (idx === pageRef.current) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (state === arrivalStateRef.current) {
      jumpToBrand(idx);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    // goToBrand refuses while carouselEnabledRef is false: during its own
    // animation, a wheel cooldown, or a dice roll. Those guards exist to stop
    // USER input landing mid-motion. This is a deliberate navigation (header
    // menu, sidebar), so rather than let it drop silently, wait for the
    // carousel to settle, bounded so a flag stuck false can never swallow the
    // request, then force it through.
    let frames = 0;
    const attempt = () => {
      if (!carouselEnabledRef.current && frames < 60) {
        frames += 1;
        requestAnimationFrame(attempt);
        return;
      }
      carouselEnabledRef.current = true;
      goToBrand(idx);
      navigate(location.pathname, { replace: true, state: {} });
    };
    requestAnimationFrame(attempt);
  }, [displayRows, location.state, location.pathname, goToBrand, jumpToBrand, navigate]);

  // Direct channel for picks made from the sidebar list / header brands menu.
  // Distinct from the state effect above, which only fires reliably on a
  // fresh arrival: this one delivers to the ALREADY-MOUNTED carousel, which is
  // the case the user hits after their first brand — on /brand there is
  // nowhere new to navigate to, so state alone could leave a second pick
  // going nowhere. Same landing rules: match by lowercased name, nothing to
  // do if it is the page being viewed, wait out a cooldown then force.
  const landOnBrand = useCallback((name) => {
    if (!name || !displayRows.length) return;
    const wanted = String(name).toLowerCase().trim();
    const idx = displayRows.findIndex((r) => r.name.toLowerCase().trim() === wanted);
    if (idx < 0 || idx === pageRef.current) return;
    let frames = 0;
    const attempt = () => {
      if (!carouselEnabledRef.current && frames < 60) {
        frames += 1;
        requestAnimationFrame(attempt);
        return;
      }
      carouselEnabledRef.current = true;
      goToBrand(idx);
    };
    requestAnimationFrame(attempt);
  }, [displayRows, goToBrand]);

  // The same, landing in place (jumpToBrand) instead of gliding: the showcase
  // list over the page (SearchModal) shows each brand it is pointed at on the
  // page behind it, and a pick from it closes on the brand already there. The
  // panel is revealed at once, without the 0.4s fade-in, as a pick from the
  // title strip is (revealRow). Straight away when the deck is settled, so the
  // page keeps up with the pointer; a glide still running is waited out.
  const jumpOnBrand = useCallback((name) => {
    if (!name || !displayRows.length) return;
    const wanted = String(name).toLowerCase().trim();
    const idx = displayRows.findIndex((r) => r.name.toLowerCase().trim() === wanted);
    if (idx < 0 || idx === pageRef.current) return;
    let frames = 0;
    const attempt = () => {
      if (!carouselEnabledRef.current && frames < 60) {
        frames += 1;
        requestAnimationFrame(attempt);
        return;
      }
      carouselEnabledRef.current = true;
      // Committed inside the update, so the picture the browser takes after
      // it is the new brand with its backdrop mounted (nearestBrand moves in
      // this commit, and the blackouts are set in its layout effect).
      const land = () => {
        revealRow(idx);
        flushSync(() => jumpToBrand(idx));
      };
      // Seen only through the showcase list's glass, it crossfades.
      if (getSearchModalShown()) crossfadeTo(land);
      else land();
    };
    attempt();
  }, [displayRows, jumpToBrand, revealRow]);

  useEffect(() => {
    if (!displayRows.length) return undefined;
    // A request made before the rows existed: an arrival from another page
    // (the sidebar or header brands list, a landing-page brand tile). It
    // lands directly; sliding there from the first brand was a fly-through
    // of the whole deck on a page still fading in.
    const parked = takePendingBrandNav();
    if (parked) {
      const idx = displayRows.findIndex((r) => r.name.toLowerCase().trim() === parked);
      if (idx > 0) jumpToBrand(idx);
    }
    // Requests made while mounted (a second pick on /brand) slide as before.
    // The signal parks every request as well as announcing it, and nothing
    // took one off the shelf once it had been delivered live, so the next
    // visit to /brand, even a plain one from the header, "arrived" on that
    // brand again and slid across the deck to it. Taking it here clears it.
    return onBrandNav((name, { jump } = {}) => {
      takePendingBrandNav();
      if (jump) jumpOnBrand(name);
      else landOnBrand(name);
    });
  }, [displayRows, landOnBrand, jumpOnBrand, jumpToBrand]);

  // ── Seamless wrap. The deck is a ring but the panels are laid out in a
  // line, so closing the ring by scrolling (last -> first, or the left arrow
  // on the first brand) meant flying back across every panel in between.
  // Instead the two end panels are slid with transforms while scrollLeft
  // stays parked: the outgoing panel moves off one way and the incoming one,
  // translated to sit just beyond it, moves in from the other, their
  // blackouts driven by hand to match a normal slide. On the last frame the
  // transforms come off and scrollLeft jumps to the incoming panel's real
  // position in the same task, which paints identically, so the seam is
  // invisible. dir +1 is last -> first (the walk); dir -1 is first -> last
  // (the left arrow and edge zone on the first brand). The incoming panel's
  // background is live because ringDistance treats the ends as neighbours. ──
  const wrapToBrand = useCallback((dir) => {
    const el = carouselRef.current;
    const N = displayRows.length;
    if (!el || N < 2 || !carouselEnabledRef.current) return;
    const from = dir > 0 ? N - 1 : 0;
    const to = dir > 0 ? 0 : N - 1;
    const outEl = rowRefs.current[from];
    const inEl = rowRefs.current[to];
    const W = el.clientWidth;
    // Only from the ring's actual end is a wrap seamless; from anywhere else
    // this is an ordinary slide.
    if (pageRef.current !== from || !outEl || !inEl || !W) {
      goToBrand(to);
      return;
    }
    // The incoming panel may never have been on screen (going back from the
    // first brand on a fresh visit): reveal it synchronously so it does not
    // fade in while it slides.
    if (!inEl.classList.contains("brand-row--visible")) {
      flushSync(() => setVisibleRows((prev) => new Set([...prev, to])));
    }
    pageRef.current = to;
    setDotActiveIdx(to);
    setSnappedBrand(null);
    carouselEnabledRef.current = false;
    wrapActiveRef.current = true;
    const outBlack = blackoutRefs.current[from];
    const inBlack = blackoutRefs.current[to];
    // Same curve as updateBlackouts, fed the fraction of a panel travelled.
    const fade = (d) => Math.min(1, 0.15 + Math.pow(d, 0.5) * 0.85);
    // The same slide as a step between neighbours (goToBrand): on a touch
    // screen its swipe-like 550ms ease-out, elsewhere the 400ms ease-in-out
    // that the wheel handler's SLIDE_MS is matched to.
    const isTouch = isTouchDevice();
    const duration = isTouch ? 550 : 400;
    const t0 = performance.now();
    const ease = isTouch
      ? (t) => 1 - Math.pow(1 - t, 3)
      : (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    // Touch screens page the deck with native mandatory scroll-snap, and a
    // snapped panel that moves takes the scroll with it: as the outgoing
    // panel slid left, the browser re-snapped to it and dragged the whole
    // deck back a brand, so the wrap ran the previous brand past before
    // jumping to the first. Snap is off for the slide, as goToBrand turns it
    // off for its own, and back on once the deck sits on the first brand.
    const prevSnap = el.style.scrollSnapType;
    if (isTouch) el.style.scrollSnapType = "none";
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const p = ease(t);
      const shift = p * W;
      if (dir > 0) {
        outEl.style.transform = `translateX(${-shift}px)`;
        inEl.style.transform = `translateX(${N * W - shift}px)`;
      } else {
        outEl.style.transform = `translateX(${shift}px)`;
        inEl.style.transform = `translateX(${shift - N * W}px)`;
      }
      if (outBlack) outBlack.style.opacity = String(fade(p));
      if (inBlack) inBlack.style.opacity = String(fade(1 - p));
      if (t < 1) {
        requestAnimationFrame(step);
        return;
      }
      // Same task, before the next paint: transforms off, real position on.
      outEl.style.transform = "";
      inEl.style.transform = "";
      el.scrollLeft = to * W;
      if (isTouch) el.style.scrollSnapType = prevSnap;
      updateBlackouts(el);
      wrapActiveRef.current = false;
      carouselEnabledRef.current = true;
    };
    requestAnimationFrame(step);
  }, [displayRows.length, goToBrand, updateBlackouts]);

  // ── First-visit peek on the title. Twice per page load, on
  // TITLE_PEEK_SCHEDULE_MS, the folded title strip breathes in far enough to
  // show the name AFTER this brand and settles back, with the right dot arrow
  // lit (.brand-row--title-peek in app.css). It teaches that the deck is a
  // ring with more in it, and names the brand the walk is about to bring in,
  // without moving anything on screen to do so.
  //
  // Only while they have done nothing, only with the strip folded and the deck
  // settled, all checked when each step fires: if by then they have wheeled,
  // clicked or opened the strip, that step and every later one are dropped for
  // this load rather than deferred — once you have moved the deck yourself,
  // you know it moves. The latch is set when the first step FIRES, not when it
  // is armed, so a mount torn down early (StrictMode, a quick bounce away)
  // cannot use up the one sequence. ──
  const [titlePeek, setTitlePeek] = useState(false);
  // Not while the site's overlay is over the page (the search, or the
  // showcase list SHOWCASE opens over this page): a peek there would fire
  // unseen and use up the load's one sequence. The schedule starts over once
  // the overlay has gone, and presses made inside it do not count as touching
  // the deck, since they were not on it.
  const overlayUp = useSyncExternalStore(subscribeSearchModalShown, getSearchModalShown, getSearchModalShown);
  useEffect(() => {
    if (!TITLE_PEEK_ENABLED || overlayUp || titlePeekShownThisLoad || displayRows.length < 2) return undefined;
    const timers = [];
    let touched = false;
    const onTouched = () => {
      touched = true;
      timers.forEach(clearTimeout);
      setTitlePeek(false);
    };
    const runStep = (step) => {
      if (step >= TITLE_PEEK_SCHEDULE_MS.length) return;
      timers.push(setTimeout(() => {
        if (step === 0) {
          if (titlePeekShownThisLoad) return;
          titlePeekShownThisLoad = true;
        }
        if (touched || stripOpenRef.current || !carouselEnabledRef.current) return;
        setTitlePeek(true);
        timers.push(setTimeout(() => setTitlePeek(false), TITLE_PEEK_MS));
        // The next wait starts now, from the moment this peek fired.
        runStep(step + 1);
      }, TITLE_PEEK_SCHEDULE_MS[step]));
    };
    runStep(0);
    window.addEventListener("wheel", onTouched, { passive: true });
    window.addEventListener("pointerdown", onTouched, { passive: true });
    window.addEventListener("keydown", onTouched);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("wheel", onTouched);
      window.removeEventListener("pointerdown", onTouched);
      window.removeEventListener("keydown", onTouched);
    };
  }, [displayRows.length, overlayUp]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el || !displayRows.length) return;
    let cooldown = false;

    const isTouch = isTouchDevice();

    // Ensure starting position
    if (isTouch) {
      el.scrollLeft = pageRef.current * el.clientWidth;
    } else {
      el.style.overflowX = "scroll";
      el.scrollLeft = pageRef.current * el.clientWidth;
      el.style.overflowX = "hidden";
    }
    updateBlackouts(el);

    const slideTo = (page) => {
      pageRef.current = page;
      const target = page === displayRows.length
        ? Math.max(0, el.scrollWidth - el.clientWidth)
        : page * el.clientWidth;
      const start = el.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) <= 2) {
        el.scrollLeft = target;
        updateBlackouts(el);
        cooldown = false;
        carouselEnabledRef.current = true;
        return;
      }
      const duration = 400;
      const t0 = performance.now();
      const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      el.style.overflowX = "scroll";
      const step = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        el.scrollLeft = start + distance * ease(t);
        updateBlackouts(el);
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          el.style.overflowX = "hidden";
          cooldown = false;
          carouselEnabledRef.current = true;
        }
      };
      requestAnimationFrame(step);
    };

    // Trackpad gesture-lock: one swipe = one slide.
    //
    // A hard trackpad swipe emits wheel events for the physical gesture AND
    // an inertia tail that can last 1–2 seconds. Using two signals together:
    //   1. POST_SLIDE_LOCK_MS: hard ignore window after any slide — outlasts
    //      the inertia tail so residual momentum cannot trigger slide #2.
    //   2. gap check: even outside the hard window, ignore events that are
    //      part of an ongoing continuous stream (gap under QUIET_MS).
    // A new slide only fires when the hard window has elapsed AND there has
    // been at least QUIET_MS of wheel silence immediately before this event.
    const POST_SLIDE_LOCK_MS = 1400;
    const QUIET_MS = 200;
    const SLIDE_MS = 400;
    let prevEventTime = 0;
    let slideActiveUntil = 0;
    let hardLockUntil = 0;
    const onWheel = (e) => {
      // With the title strip out, the wheel walks its names (stepStripRef)
      // instead of paging the deck.
      if (stripOpenRef.current) {
        e.preventDefault();
        stepStripRef.current?.(e);
        return;
      }
      if (e.target.closest(".brand-row__track")) return;
      e.preventDefault();
      const now = performance.now();
      const gap = now - prevEventTime;
      prevEventTime = now;

      if (now < slideActiveUntil) return;
      if (now < hardLockUntil) return;
      if (gap < QUIET_MS) return;
      if (cooldown) return;
      // A seamless wrap (wrapToBrand) is a transform slide with scrollLeft
      // parked; a wheel slide started under it would fight it for the scroll
      // position. Ignored for its 400ms only — the flag is cleared on the
      // wrap's last frame, never left behind.
      if (wrapActiveRef.current) return;

      // Horizontal paging: honor whichever axis the gesture is stronger on,
      // so a mouse wheel (deltaY) and a sideways trackpad swipe (deltaX)
      // both page left/right.
      const sideways = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      const delta = sideways ? e.deltaX : e.deltaY;
      if (!delta) return;
      // A mouse wheel rolled UP goes on to UP NEXT, down goes back (the
      // owner, 2026-10-17: "up next should be scroll up on mousewheel");
      // it was the other way round. A sideways swipe keeps its own sense,
      // right to the next brand.
      const dir = sideways ? (delta > 0 ? 1 : -1) : (delta < 0 ? 1 : -1);
      // The deck is a ring, so wheeling off either end closes it rather than
      // stopping: right from the last brand lands on the first, left from the
      // first lands on the last. This is also why the footer is no longer in
      // the wheel's path — a ring has no end to park it at.
      //
      // wrapToBrand runs its own slide and owns carouselEnabledRef, so the
      // local `cooldown` latch is deliberately NOT armed here: only slideTo's
      // last frame clears it, and setting it for a slide that never runs would
      // leave the wheel dead. The two time locks are self-expiring, so they
      // are safe to arm for the wrap's duration (SLIDE_MS matches it).
      const offEnd = dir > 0
        ? pageRef.current === displayRows.length - 1
        : pageRef.current === 0;
      if (offEnd && displayRows.length > 1) {
        slideActiveUntil = now + SLIDE_MS;
        hardLockUntil = now + POST_SLIDE_LOCK_MS;
        wrapToBrand(dir);
        return;
      }
      const next = Math.max(0, Math.min(displayRows.length, pageRef.current + dir));
      if (next === pageRef.current) return;

      slideActiveUntil = now + SLIDE_MS;
      hardLockUntil = now + POST_SLIDE_LOCK_MS;
      cooldown = true;
      carouselEnabledRef.current = false;
      // Mirror goToBrand's behavior — drive the dot indicator from the
      // click target / wheel target so the active dot updates immediately
      // (the nearestBrand-driven sync is gated by carouselEnabledRef and
      // wouldn't fire until after the slide completes).
      setDotActiveIdx(next);
      slideTo(next);
    };

    el.addEventListener("wheel", onWheel, { passive: false });

    // Desktop: keep the carousel pinned to the current page. scrollLeft is a
    // pixel value while the panels are 100vw, so if the viewport width
    // changes after a snap (window resize, devtools opening) every panel
    // past the first drifts by page x delta-width. Re-derive scrollLeft from
    // the page index whenever the container resizes, and put back any stray
    // scroll (e.g. focus-into-view) while no slide animation owns scrollLeft.
    let realignRaf = null;
    let resizeObserver = null;
    const pageTarget = () => (pageRef.current === displayRows.length
      ? Math.max(0, el.scrollWidth - el.clientWidth)
      : pageRef.current * el.clientWidth);
    const realign = () => {
      realignRaf = null;
      if (cooldown || !carouselEnabledRef.current) return;
      const target = pageTarget();
      if (Math.abs(el.scrollLeft - target) > 1) {
        el.scrollLeft = target;
        updateBlackouts(el);
      }
    };
    const scheduleRealign = () => {
      if (!realignRaf) realignRaf = requestAnimationFrame(realign);
    };
    if (!isTouch) {
      window.addEventListener("resize", scheduleRealign);
      el.addEventListener("scroll", scheduleRealign, { passive: true });
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(scheduleRealign);
        resizeObserver.observe(el);
      }
    }

    // On touch devices, native scroll-snap moves between brand rows. Listen
    // for scroll so blackouts and the snappedBrand badge stay current.
    let nativeScrollHandler = null;
    if (isTouch) {
      nativeScrollHandler = () => {
        updateBlackouts(el);
        const sectionW = el.clientWidth || 1;
        pageRef.current = Math.round(el.scrollLeft / sectionW);
      };
      el.addEventListener("scroll", nativeScrollHandler, { passive: true });
    }

    return () => {
      el.removeEventListener("wheel", onWheel);
      if (nativeScrollHandler) el.removeEventListener("scroll", nativeScrollHandler);
      window.removeEventListener("resize", scheduleRealign);
      el.removeEventListener("scroll", scheduleRealign);
      if (resizeObserver) resizeObserver.disconnect();
      if (realignRaf) cancelAnimationFrame(realignRaf);
    };
  }, [displayRows.length, wrapToBrand]);


  // Key by the entry id (set to inventory_item_id everywhere cart items are
  // created). Using c.item?.id breaks for cart entries whose inventory payload
  // hasn't been hydrated from the server.
  const cartItemIds = useMemo(() => new Set(cartItems.map((c) => c.id)), [cartItems]);

  const handleToggleCart = useCallback((item) => {
    // Find every entry that matches this inventory id regardless of
    // size/color variant. The brand card has no variant context, so we
    // remove whatever matches — otherwise items added with a size get
    // stuck in the cart because removeFromCart(id) defaults size to null
    // and the reducer requires an exact (id, size, color) match.
    const matching = cartItems.filter((c) => c.id === item.id || c.item?.id === item.id);
    if (matching.length > 0) {
      for (const entry of matching) {
        removeFromCart(entry.id, entry.size ?? null, entry.color ?? null);
      }
    } else {
      addToCart(item);
    }
  }, [cartItems, addToCart, removeFromCart]);

  // When a brand has a background set, "View all" and product count fade out.
  // Hovering the brand title shows them for 2.5s then fades them back out.
  const [headerPeek, setHeaderPeek] = useState({});
  const peekTimers = useRef({});


  const handleTitleHover = useCallback((rowIndex) => {
    clearTimeout(peekTimers.current[rowIndex]);
    setHeaderPeek((prev) => ({ ...prev, [rowIndex]: true }));
    peekTimers.current[rowIndex] = setTimeout(() => {
      setHeaderPeek((prev) => ({ ...prev, [rowIndex]: false }));
    }, 2500);
  }, []);

  // ── The strip opens on hover ──
  // Pointing at the brand's title fans the strip out, with every transition
  // the title's press used to bring, and the pointer leaving the title and its
  // names folds it again, STRIP_HOVER_GRACE_MS later so a brush past the
  // row's edge does not snap it shut (the owner, 2026-09-30: "remove the
  // press brand feature ... just use hover only press wont do anything ... it
  // is our new feature that will replace"). It replaced the hover peek, which
  // held only the next name beside the title. The pointer arrives on the
  // title's twin in the folded strip, which lies over the title and takes its
  // pointer on desktop; open, the strip covers the whole title row, and its
  // own enter and leave drive this. Pointer devices only: a touchscreen has
  // no hover, and its title keeps its press (handleTitleClick).
  // A name picked from the strip lands on its brand and folds the strip, and
  // the new brand's title then arrives under the resting pointer. The enter
  // that comes of that, at the spot of the press, does not open the strip
  // again (stripLandRef); the next real arrival does.
  const stripHoverTimer = useRef(0);
  const stripLandRef = useRef(null);
  // Off (the owner, 2026-10-02: "remove this hover brands thing, when you
  // hover over title it shows no more"): the pointer on the title opens
  // nothing, and the title stays a title. Flip STRIP_ON_HOVER back to bring
  // the hover strip back.
  const hoverOpenStrip = useCallback((e) => {
    clearTimeout(stripHoverTimer.current);
    if (!STRIP_ON_HOVER) return;
    if (!hasHoverPointer() || isMobileViewport()) return;
    const land = stripLandRef.current;
    if (land && e && performance.now() - land.t < 1000 && Math.hypot(e.clientX - land.x, e.clientY - land.y) < 6) return;
    stripLandRef.current = null;
    setStripOpen(true);
  }, []);
  const hoverFoldStrip = useCallback(() => {
    clearTimeout(stripHoverTimer.current);
    stripHoverTimer.current = setTimeout(() => {
      setStripPreview(null);
      setStripOpen(false);
    }, STRIP_HOVER_GRACE_MS);
  }, []);
  useEffect(() => () => clearTimeout(stripHoverTimer.current), []);

  if (!displayRows.length && !listLoading) return null;

  return (
    <>
    {/* Inferior-mirage shimmer for the up/down nav-zone glass strips:
        animated turbulence drives a displacement map so the brand image
        visible behind the bar trembles like a heat-haze. Defined once and
        referenced from CSS via backdrop-filter: url(#brand-mirage). */}
    <svg
      width="0"
      height="0"
      style={{ position: "absolute", pointerEvents: "none" }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id="brand-mirage" x="0" y="0" width="100%" height="100%">
          {/* numOctaves=1 keeps the turbulence one-pass (cheap enough to
              animate every frame); scale=4 keeps the displacement subtle so
              it reads as shimmer not warp. */}
          <feTurbulence type="turbulence" baseFrequency="0.012 0.028" numOctaves="1" seed="3" result="noise">
            <animate
              attributeName="baseFrequency"
              values="0.012 0.028; 0.014 0.032; 0.012 0.028"
              dur="8s"
              repeatCount="indefinite"
            />
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="4" />
        </filter>
      </defs>
    </svg>
    {displayRows.length > 0 && (
      <button
        type="button"
        className={`brand-footer-nav${atFooter ? " brand-footer-nav--visible" : ""}`}
        aria-label="Back to last brand"
        onClick={() => goToBrand(displayRows.length - 1)}
      >
        <svg width="13" height="27" viewBox="0 0 8 14" fill="none"
          stroke="currentColor" strokeWidth="1.24" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 1L1 7L7 13" />
        </svg>
      </button>
    )}
    <div ref={carouselRef} className={`brand-carousel${!listLoading ? " brand-carousel--ready" : ""}${mouseActive ? " brand-carousel--mouse-active" : ""}`}>
      {displayRows.map((row, rowIndex) => (
        <section
          key={row.name}
          ref={(el) => { rowRefs.current[rowIndex] = el; }}
          data-row-index={rowIndex}
          className={`brand-row${visibleRows.has(rowIndex) ? " brand-row--visible" : ""}${brandBgs[row.name] && snappedBrand === rowIndex ? " brand-row--has-bg" : ""}${headerPeek[rowIndex] ? " brand-row--header-peek" : ""}${titlePeek && snappedBrand === rowIndex ? " brand-row--title-peek" : ""}${stripPreview && stripPreview.row === rowIndex ? " brand-row--strip-preview" : ""}${stripOpen && snappedBrand === rowIndex ? " brand-row--strip-open" : ""}`}
        >
          {/* Only mount the background for the nearest brand and its immediate
              neighbors. Off-screen brand backgrounds were the largest resident
              bitmap cost on /brand — keeping 2–3 live instead of N. Neighbours
              are counted around the ring, so the first and last brands hold
              each other's background ready for the seamless wrap. */}
          {brandBgs[row.name] && ringDistance(nearestBrand, rowIndex, displayRows.length) <= 1 && (
            <BrandRowBackground
              bg={brandBgs[row.name]}
              items={row.items}
              active={visibleRows.has(rowIndex)}
              ref={(el) => { blackoutRefs.current[rowIndex] = el; }}
            />
          )}
          {/* Click-off layer while the title strip is out: a press anywhere
              but the names folds the strip, as pressing the title does. It
              lives in the on-screen row, over the row's backdrop and
              products (z 20) and under the row's header, which is lifted
              above it while the strip is out (.brand-row--strip-open), so the
              names take the pointer. Invisible. */}
          {stripOpen && snappedBrand === rowIndex && (
            <button
              type="button"
              className="brand-row__strip-glass brand-row__strip-glass--shown"
              aria-label="Close brand list"
              onClick={() => setStripOpen(false)}
            />
          )}
          {/* Hover preview from the title strip: black over the row's own
              backdrop, then the hovered brand's backdrop fading up on it. It
              is mounted for the on-screen row while the strip is out, so it
              can fade both ways; the backdrop inside is keyed by brand, so
              moving from one name to the next dips through black again. Its
              blackout is set to the row's own, so it reads at the same
              depth. */}
          {stripOpen && snappedBrand === rowIndex && (() => {
            const on = stripPreview && stripPreview.row === rowIndex;
            const target = on ? displayRows[stripPreview.idx] : null;
            const bg = target ? brandBgs[target.name] : null;
            return (
              <div className={`brand-row__strip-preview${on ? " brand-row__strip-preview--on" : ""}`} aria-hidden="true">
                {bg && (
                  <BrandRowBackground
                    key={target.name}
                    bg={bg}
                    items={target.items}
                    ref={(el) => { if (el) el.style.opacity = blackoutRefs.current[rowIndex]?.style.opacity || "0.15"; }}
                  />
                )}
              </div>
            );
          })()}
          {/* Back on every row, the first included: the deck is a ring (from
              the last brand, next is the first), so from the first brand
              "previous" is the last one. Used to be hidden on the first row,
              which left no way back from there. Same for the dot strip's
              left arrow below. */}
          <button
            type="button"
            className={`brand-row__nav-zone brand-row__nav-zone--left${snappedBrand === rowIndex ? " brand-row__nav-zone--visible" : ""}`}
            aria-label="Previous brand"
            onClick={() => (rowIndex > 0 ? goToBrand(rowIndex - 1) : wrapToBrand(-1))}
          >
            <svg width="13" height="27" viewBox="0 0 8 14" fill="none"
              stroke="currentColor" strokeWidth="1.24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 1L1 7L7 13" />
            </svg>
          </button>
          {/* Per-row music toggle with audio visualizer — only the snapped
              brand's instance runs the analyser tick to keep perf in check. */}
          {MUSIC_ENABLED && <BrandRowAudio active={nearestBrand === rowIndex} />}
          <button
            type="button"
            className={`brand-row__nav-zone brand-row__nav-zone--right${snappedBrand === rowIndex ? " brand-row__nav-zone--visible" : ""}`}
            aria-label={rowIndex < displayRows.length - 1 ? "Next brand" : "View footer"}
            onClick={() => (rowIndex < displayRows.length - 1 ? goToBrand(rowIndex + 1) : wrapToBrand(1))}
          >
            <svg width="14" height="29" viewBox="0 0 8 14" fill="none"
              stroke="currentColor" strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 1L7 7L1 13" />
            </svg>
          </button>
          {(() => {
            const shown = shownRowFor(row, rowIndex);
            // The brands either side, around the ring, for the pager.
            const deckSize = displayRows.length;
            const nextRow = displayRows[(rowIndex + 1) % deckSize];
            const prevRow = displayRows[(rowIndex - 1 + deckSize) % deckSize];
            const goNext = () => (rowIndex < deckSize - 1 ? goToBrand(rowIndex + 1) : wrapToBrand(1));
            const goPrev = () => (rowIndex > 0 ? goToBrand(rowIndex - 1) : wrapToBrand(-1));
            // "Up next" names the brand after the one on screen — the hovered
            // or wheel-lit one while the strip previews it, so it never
            // repeats the lit name — and goes there.
            const shownIndex = Math.max(0, displayRows.indexOf(shown));
            const upNextRow = displayRows[(shownIndex + 1) % deckSize];
            const goUpNext = () => (shownIndex === rowIndex ? goNext() : goToBrand((shownIndex + 1) % deckSize));
            // "Up next: BRAND", on the count line level with the product
            // count, at every width. It names where the next move goes (the
            // arrows on the title line, a swipe) and is itself a
            // way there. It used to sit between the arrows on the title line,
            // where the name crowded the pager and read as a second title.
            const upNextButton = () => (
              <button
                type="button"
                className="brand-nav-next brand-nav-next--count"
                aria-label={`Next brand: ${upNextRow.name}`}
                onClick={goUpNext}
              >
                {/* The space after the colon is a real one (non-breaking, so it
                    survives at the end of the span), not the flex gap: that
                    was 0.4em, wider than a word space, and read as two. */}
                <span className="brand-nav-next__label">Up next:{"\u00a0"}</span>
                <span className="brand-nav-next__name">{upNextRow.name}</span>
              </button>
            );
            // The title always names the brand on screen. Hovering a nav dot
            // used to swap it (and the breadcrumb) for that dot's brand in
            // grey; that preview is gone.
            return (
              <div className="brand-row__header">
                {/* (A SHOWCASE breadcrumb sat over the title here, opening the
                    brand index; removed 2026-10-17, the owner: "remove
                    showcase ... just this header". It took no room, a -14px
                    margin folding it into the title's line, so the title
                    stays where it was. The header's SHOWCASE link still
                    opens the index.) */}
                <div
                  className={`brand-row__title-row${stripOpen ? " brand-row__title-row--strip-open" : ""}`}
                  style={{ "--title-ink": titleInkEm(titleInk.font, row.name) }}
                >
                  <span
                    className="brand-row__title"
                    onMouseEnter={() => brandBgs[row.name] && handleTitleHover(rowIndex)}
                    onClick={handleTitleClick}
                    aria-expanded={stripOpen}
                  >
                    <span className="brand-row__title-text brand-row__title-text--base">
                      {row.name.toUpperCase()}
                    </span>
                  </span>
                  {/* The rest of the deck on the same line as the title, in
                      ring order from this brand onward, so the name after
                      the current one is always first. Each is a jump straight
                      to that brand; hovering previews its backdrop the way the
                      title itself does. */}
                  {displayRows.length > 1 && (
                    <div className={`brand-row__title-strip${stripOpen ? " brand-row__title-strip--open" : ""}${stripPreview && stripPreview.row === rowIndex ? " brand-row__title-strip--indicating" : ""}`} ref={attachStripWheel} data-row-index={rowIndex} aria-hidden={!stripOpen} onMouseEnter={hoverOpenStrip} onMouseLeave={() => { setStripPreview(null); hoverFoldStrip(); }}>
                      {/* Each brand once, in ring order. Folded, and until a
                          scroll back turns it, the strip starts at the current
                          brand, which sits exactly over the title, and the
                          brands after it follow. Scrolling back brings the
                          brand before the current one round from the end to
                          the front, in from behind the left edge (stripBack).
                          Each name is a jump straight to that brand, landing
                          in place; hovering one previews its page. The current
                          one takes the title's pointer: arriving on it opens
                          the strip (hoverOpenStrip, on the strip; a press, on
                          a touchscreen), and a drag that starts on it grabs
                          the row (attachStripDrag). */}
                      {(() => {
                        const n = displayRows.length;
                        const back = stripBack.row === rowIndex ? stripBack.back : 0;
                        const names = [];
                        for (let p = 0; p < n; p += 1) {
                          const k = p - back;
                          const brandIndex = (((rowIndex + k) % n) + n) % n;
                          const brand = displayRows[brandIndex];
                          if (k === 0) {
                            names.push(
                              <button
                                key={brand.name}
                                data-brand={brand.name}
                                data-k={0}
                                type="button"
                                className="brand-row__title-strip-item brand-row__title-strip-item--current"
                                tabIndex={stripOpen ? 0 : -1}
                                aria-current="true"
                                onClick={() => {
                                  // A pointer opens and folds the strip by
                                  // hovering; the press is a touchscreen's.
                                  if (hasHoverPointer()) return;
                                  setStripPreview(null);
                                  setStripOpen((open) => !open);
                                }}
                                onMouseEnter={() => { stripPosRef.current = 0; setStripPreview(null); }}
                              >
                                {brand.name.toUpperCase()}
                              </button>,
                            );
                            continue;
                          }
                          const lit = Boolean(stripPreview && stripPreview.row === rowIndex && stripPreview.k === k);
                          names.push(
                            <button
                              key={brand.name}
                              data-brand={brand.name}
                              data-k={k}
                              type="button"
                              className={`brand-row__title-strip-item${k === 1 ? " brand-row__title-strip-item--next" : ""}${lit ? " brand-row__title-strip-item--indicated" : ""}`}
                              tabIndex={stripOpen ? 0 : -1}
                              onClick={(e) => {
                                // A name half under the right-edge fade pages
                                // the strip on instead (pageStripTo).
                                if (pageStripTo(e.currentTarget.parentElement, e.currentTarget)) return;
                                revealRow(brandIndex);
                                setStripPreview(null);
                                // Where the press was, so the new brand's title,
                                // arriving under the resting pointer, does not
                                // open the strip again (hoverOpenStrip).
                                stripLandRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
                                clearTimeout(stripHoverTimer.current);
                                setStripOpen(false);
                                jumpToBrand(brandIndex);
                              }}
                              onMouseEnter={() => {
                                // The wheel carries on from the name the pointer is on.
                                stripPosRef.current = k;
                                setStripPreview({ row: rowIndex, idx: brandIndex, k });
                                setVisibleRows((prev) => (prev.has(brandIndex) ? prev : new Set([...prev, brandIndex])));
                              }}
                            >
                              {brand.name.toUpperCase()}
                            </button>,
                          );
                        }
                        return names;
                      })()}
                    </div>
                  )}
                  {/* The pager on the title line: a back arrow and a forward
                      arrow where a row of dots used to be. Straight arrows,
                      so they cannot be taken for the product chevrons beside
                      the centre card. The next brand is named below them, on
                      the count line ("Up next:", upNextButton), not between
                      them. Desktop only: a phone has the count line's name
                      and its swipe. Every row renders its own; --visible follows snappedBrand, so the outgoing
                      brand's pager fades as the incoming one's arrives, and
                      it steps aside while the title strip is out (app.css). */}
                  {!isPhone && deckSize > 1 && (
                    <nav
                      className={`brand-nav-dots${snappedBrand === rowIndex ? " brand-nav-dots--visible" : ""}`}
                      aria-label="Brand navigation"
                    >
                      <button
                        type="button"
                        className="brand-nav-dots__arrow brand-nav-dots__arrow--left"
                        aria-label={`Previous brand: ${prevRow.name}`}
                        title={prevRow.name.toUpperCase()}
                        onClick={goPrev}
                      >
                        <svg width="15" height="12" viewBox="0 0 15 12" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 6H1.5M6 1L1 6l5 5" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="brand-nav-dots__arrow brand-nav-dots__arrow--right"
                        aria-label={`Next brand: ${nextRow.name}`}
                        title={nextRow.name.toUpperCase()}
                        onClick={goNext}
                      >
                        <svg width="15" height="12" viewBox="0 0 15 12" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 6h12.5M9 1l5 5-5 5" />
                        </svg>
                      </button>
                    </nav>
                  )}
                </div>
                <div className="brand-row__count-bar">
                  <span className="brand-row__count">
                    {shown.items.length} {shown.items.length === 1 ? "Product" : "Products"}
                  </span>
                  {deckSize > 1 && upNextButton()}
                </div>
              </div>
            );
          })()}
          <BrandRowTrack key={shownRowFor(row, rowIndex).name} items={shownRowFor(row, rowIndex).items} bg={brandBgs[shownRowFor(row, rowIndex).name] || null} active={visibleRows.has(rowIndex)} waiting={!bgsLoaded} cartItemIds={cartItemIds} onToggleCart={handleToggleCart} carouselEnabledRef={carouselEnabledRef} resetKey={0} staggerMs={stripOpen && snappedBrand === rowIndex ? 0 : 120} />
        </section>
      ))}
      <section className="brand-row brand-row--footer">
        <Footer />
      </section>
    </div>
    </>
  );
}
